@file:Suppress("ComplexRedundantLet", "SimpleRedundantLet", "unused", "UnnecessaryVariable", "NestedLambdaShadowedImplicitParameter", "PropertyName", "RedundantVisibilityModifier")

package com.steamstreet.vegasful.graphql.api.client

import com.steamstreet.dates.DurationSerializer
import com.steamstreet.graphkt.ID
import com.steamstreet.graphkt.client.GraphQLResponse
import com.steamstreet.vegasful.graphql.api.AWSDate
import com.steamstreet.vegasful.graphql.api.AWSDateTime
import com.steamstreet.vegasful.graphql.api.AWSTime
import com.steamstreet.vegasful.graphql.api.ContentPanelFlag
import com.steamstreet.vegasful.graphql.api.ContentPanelType
import com.steamstreet.vegasful.graphql.api.ContentSource
import com.steamstreet.vegasful.graphql.api.ContentViewEntityAction
import com.steamstreet.vegasful.graphql.api.ContentViewLinkType
import com.steamstreet.vegasful.graphql.api.ContentViewType
import com.steamstreet.vegasful.graphql.api.ConversationStatus
import com.steamstreet.vegasful.graphql.api.DateRangePreset
import com.steamstreet.vegasful.graphql.api.EntitySource
import com.steamstreet.vegasful.graphql.api.EventDuration
import com.steamstreet.vegasful.graphql.api.ImageVariant
import com.steamstreet.vegasful.graphql.api.InteractionType
import com.steamstreet.vegasful.graphql.api.SocialImageState
import com.steamstreet.vegasful.graphql.api.SourceEntityType
import com.steamstreet.vegasful.graphql.api.SourceMode
import com.steamstreet.vegasful.graphql.api.VenueStatus
import com.steamstreet.vegasful.graphql.api.json
import kotlin.Boolean
import kotlin.Float
import kotlin.Int
import kotlin.NullPointerException
import kotlin.String
import kotlin.Suppress
import kotlin.collections.List
import kotlinx.datetime.serializers.InstantIso8601Serializer
import kotlinx.datetime.serializers.LocalDateIso8601Serializer
import kotlinx.datetime.serializers.LocalTimeIso8601Serializer
import kotlinx.serialization.json.JsonNull
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.floatOrNull
import kotlinx.serialization.json.intOrNull
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive

public interface Query {
  public val venues: Venues

  public val events: Events

  public val performers: Performers

  public val tags: Tags

  public val articles: Articles

  public val content: Content

  public val search: GlobalSearchResults

  public val conversations: Conversations

  public val subscriptions: Subscriptions

  public val entity: Entity

  public val entities: List<Entity>

  public val permissions: List<String>

  public val contentViews: ContentViews

  public val clientSupport: ClientSupport

  public val auxContent: AuxContent
}

public class QueryResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : Query {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val venues: Venues
    get() {
      _response.throwIfError("venues")
      val result = element["venues"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          VenuesResponse(_response.forElement("venues"), it)
        }
      }
      return result ?: throw NullPointerException("Query: venues")
    }

  override val events: Events
    get() {
      _response.throwIfError("events")
      val result = element["events"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          EventsResponse(_response.forElement("events"), it)
        }
      }
      return result ?: throw NullPointerException("Query: events")
    }

  override val performers: Performers
    get() {
      _response.throwIfError("performers")
      val result = element["performers"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          PerformersResponse(_response.forElement("performers"), it)
        }
      }
      return result ?: throw NullPointerException("Query: performers")
    }

  override val tags: Tags
    get() {
      _response.throwIfError("tags")
      val result = element["tags"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          TagsResponse(_response.forElement("tags"), it)
        }
      }
      return result ?: throw NullPointerException("Query: tags")
    }

  override val articles: Articles
    get() {
      _response.throwIfError("articles")
      val result = element["articles"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          ArticlesResponse(_response.forElement("articles"), it)
        }
      }
      return result ?: throw NullPointerException("Query: articles")
    }

  override val content: Content
    get() {
      _response.throwIfError("content")
      val result = element["content"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          ContentResponse(_response.forElement("content"), it)
        }
      }
      return result ?: throw NullPointerException("Query: content")
    }

  override val search: GlobalSearchResults
    get() {
      _response.throwIfError("search")
      val result = element["search"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          GlobalSearchResultsResponse(_response.forElement("search"), it)
        }
      }
      return result ?: throw NullPointerException("Query: search")
    }

  override val conversations: Conversations
    get() {
      _response.throwIfError("conversations")
      val result = element["conversations"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          ConversationsResponse(_response.forElement("conversations"), it)
        }
      }
      return result ?: throw NullPointerException("Query: conversations")
    }

  override val subscriptions: Subscriptions
    get() {
      _response.throwIfError("subscriptions")
      val result = element["subscriptions"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          SubscriptionsResponse(_response.forElement("subscriptions"), it)
        }
      }
      return result ?: throw NullPointerException("Query: subscriptions")
    }

  override val entity: Entity
    get() {
      _response.throwIfError("entity")
      val result = element["entity"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          val typeName = it.get("__typename")?.jsonPrimitive?.contentOrNull
          when (typeName) {
            "Venue" -> VenueResponse(_response.forElement("entity"), it)
            "Tag" -> TagResponse(_response.forElement("entity"), it)
            "Event" -> EventResponse(_response.forElement("entity"), it)
            "Performer" -> PerformerResponse(_response.forElement("entity"), it)
            "Article" -> ArticleResponse(_response.forElement("entity"), it)
            "ContentViewEntity" -> ContentViewEntityResponse(_response.forElement("entity"), it)
            else -> EntityResponse(_response.forElement("entity"), it)
          }
        }
      }
      return result ?: throw NullPointerException("Query: entity")
    }

  override val entities: List<Entity>
    get() {
      _response.throwIfError("entities")
      val result = element["entities"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            val typeName = it.get("__typename")?.jsonPrimitive?.contentOrNull
            when (typeName) {
              "Venue" -> VenueResponse(_response.forElement("entities"), it)
              "Tag" -> TagResponse(_response.forElement("entities"), it)
              "Event" -> EventResponse(_response.forElement("entities"), it)
              "Performer" -> PerformerResponse(_response.forElement("entities"), it)
              "Article" -> ArticleResponse(_response.forElement("entities"), it)
              "ContentViewEntity" -> ContentViewEntityResponse(_response.forElement("entities"), it)
              else -> EntityResponse(_response.forElement("entities"), it)
            }
          }
        }
      }
      return result ?: throw NullPointerException("Query: entities")
    }

  override val permissions: List<String>
    get() {
      _response.throwIfError("permissions")
      val result = element["permissions"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonPrimitive.content
        }
      }
      return result ?: throw NullPointerException("Query: permissions")
    }

  override val contentViews: ContentViews
    get() {
      _response.throwIfError("contentViews")
      val result = element["contentViews"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          ContentViewsResponse(_response.forElement("contentViews"), it)
        }
      }
      return result ?: throw NullPointerException("Query: contentViews")
    }

  override val clientSupport: ClientSupport
    get() {
      _response.throwIfError("clientSupport")
      val result = element["clientSupport"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          ClientSupportResponse(_response.forElement("clientSupport"), it)
        }
      }
      return result ?: throw NullPointerException("Query: clientSupport")
    }

  override val auxContent: AuxContent
    get() {
      _response.throwIfError("auxContent")
      val result = element["auxContent"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AuxContentResponse(_response.forElement("auxContent"), it)
        }
      }
      return result ?: throw NullPointerException("Query: auxContent")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface Content {
  public val venues: Venues

  public val events: Events

  public val performers: Performers

  public val tags: Tags

  public val entities: List<Entity>
}

public class ContentResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : Content {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val venues: Venues
    get() {
      _response.throwIfError("venues")
      val result = element["venues"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          VenuesResponse(_response.forElement("venues"), it)
        }
      }
      return result ?: throw NullPointerException("Content: venues")
    }

  override val events: Events
    get() {
      _response.throwIfError("events")
      val result = element["events"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          EventsResponse(_response.forElement("events"), it)
        }
      }
      return result ?: throw NullPointerException("Content: events")
    }

  override val performers: Performers
    get() {
      _response.throwIfError("performers")
      val result = element["performers"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          PerformersResponse(_response.forElement("performers"), it)
        }
      }
      return result ?: throw NullPointerException("Content: performers")
    }

  override val tags: Tags
    get() {
      _response.throwIfError("tags")
      val result = element["tags"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          TagsResponse(_response.forElement("tags"), it)
        }
      }
      return result ?: throw NullPointerException("Content: tags")
    }

  override val entities: List<Entity>
    get() {
      _response.throwIfError("entities")
      val result = element["entities"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            val typeName = it.get("__typename")?.jsonPrimitive?.contentOrNull
            when (typeName) {
              "Venue" -> VenueResponse(_response.forElement("entities"), it)
              "Tag" -> TagResponse(_response.forElement("entities"), it)
              "Event" -> EventResponse(_response.forElement("entities"), it)
              "Performer" -> PerformerResponse(_response.forElement("entities"), it)
              "Article" -> ArticleResponse(_response.forElement("entities"), it)
              "ContentViewEntity" -> ContentViewEntityResponse(_response.forElement("entities"), it)
              else -> EntityResponse(_response.forElement("entities"), it)
            }
          }
        }
      }
      return result ?: throw NullPointerException("Content: entities")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface Mutation {
  public val admin: AdminMutation

  public val subscriptions: SubscriptionsMutator

  public val accounts: Accounts

  public val clientSupport: ClientSupport
}

public class MutationResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : Mutation {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val admin: AdminMutation
    get() {
      _response.throwIfError("admin")
      val result = element["admin"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminMutationResponse(_response.forElement("admin"), it)
        }
      }
      return result ?: throw NullPointerException("Mutation: admin")
    }

  override val subscriptions: SubscriptionsMutator
    get() {
      _response.throwIfError("subscriptions")
      val result = element["subscriptions"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          SubscriptionsMutatorResponse(_response.forElement("subscriptions"), it)
        }
      }
      return result ?: throw NullPointerException("Mutation: subscriptions")
    }

  override val accounts: Accounts
    get() {
      _response.throwIfError("accounts")
      val result = element["accounts"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AccountsResponse(_response.forElement("accounts"), it)
        }
      }
      return result ?: throw NullPointerException("Mutation: accounts")
    }

  override val clientSupport: ClientSupport
    get() {
      _response.throwIfError("clientSupport")
      val result = element["clientSupport"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          ClientSupportResponse(_response.forElement("clientSupport"), it)
        }
      }
      return result ?: throw NullPointerException("Mutation: clientSupport")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface Conversations {
  public val start: Conversation

  public val conversation: Conversation?
}

public class ConversationsResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : Conversations {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val start: Conversation
    get() {
      _response.throwIfError("start")
      val result = element["start"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          ConversationResponse(_response.forElement("start"), it)
        }
      }
      return result ?: throw NullPointerException("Conversations: start")
    }

  override val conversation: Conversation?
    get() {
      _response.throwIfError("conversation")
      val result = element["conversation"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          ConversationResponse(_response.forElement("conversation"), it)
        }
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface Conversation {
  public val id: ID

  public val panels: List<ContentPanel>?

  public val status: ConversationStatus?

  public val response: String?
}

public class ConversationResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : Conversation {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val id: ID
    get() {
      _response.throwIfError("id")
      val result = element["id"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("Conversation: id")
    }

  override val panels: List<ContentPanel>?
    get() {
      _response.throwIfError("panels")
      val result = element["panels"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            ContentPanelResponse(_response.forElement("panels"), it)
          }
        }
      }
      return result
    }

  override val status: ConversationStatus?
    get() {
      _response.throwIfError("status")
      val result = element["status"]?.takeIf { it !is JsonNull }?.let {
        ConversationStatus.valueOf(it.jsonPrimitive.content)
      }
      return result
    }

  override val response: String?
    get() {
      _response.throwIfError("response")
      val result = element["response"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface GlobalSearchResults {
  public val events: List<Event>?

  public val venues: List<Venue>?

  public val tags: List<Tag>?

  public val performers: List<Performer>?
}

public class GlobalSearchResultsResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : GlobalSearchResults {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val events: List<Event>?
    get() {
      _response.throwIfError("events")
      val result = element["events"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            EventResponse(_response.forElement("events"), it)
          }
        }
      }
      return result
    }

  override val venues: List<Venue>?
    get() {
      _response.throwIfError("venues")
      val result = element["venues"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            VenueResponse(_response.forElement("venues"), it)
          }
        }
      }
      return result
    }

  override val tags: List<Tag>?
    get() {
      _response.throwIfError("tags")
      val result = element["tags"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            TagResponse(_response.forElement("tags"), it)
          }
        }
      }
      return result
    }

  override val performers: List<Performer>?
    get() {
      _response.throwIfError("performers")
      val result = element["performers"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            PerformerResponse(_response.forElement("performers"), it)
          }
        }
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface Entity {
  public val __typename: String

  public val id: ID

  public val name: String?

  public val description: String?

  public val images: List<Image>?

  public val tags: List<Tag>?

  public val path: String?

  public val events: EventSearchResults
}

public class EntityResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : Entity {
  override val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val id: ID
    get() {
      _response.throwIfError("id")
      val result = element["id"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("Entity: id")
    }

  override val name: String?
    get() {
      _response.throwIfError("name")
      val result = element["name"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val description: String?
    get() {
      _response.throwIfError("description")
      val result = element["description"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val images: List<Image>?
    get() {
      _response.throwIfError("images")
      val result = element["images"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            ImageResponse(_response.forElement("images"), it)
          }
        }
      }
      return result
    }

  override val tags: List<Tag>?
    get() {
      _response.throwIfError("tags")
      val result = element["tags"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            TagResponse(_response.forElement("tags"), it)
          }
        }
      }
      return result
    }

  override val path: String?
    get() {
      _response.throwIfError("path")
      val result = element["path"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val events: EventSearchResults
    get() {
      _response.throwIfError("events")
      val result = element["events"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          EventSearchResultsResponse(_response.forElement("events"), it)
        }
      }
      return result ?: throw NullPointerException("Entity: events")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface Venues {
  public val root: Venue

  public val venue: Venue
}

public class VenuesResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : Venues {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val root: Venue
    get() {
      _response.throwIfError("root")
      val result = element["root"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          VenueResponse(_response.forElement("root"), it)
        }
      }
      return result ?: throw NullPointerException("Venues: root")
    }

  override val venue: Venue
    get() {
      _response.throwIfError("venue")
      val result = element["venue"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          VenueResponse(_response.forElement("venue"), it)
        }
      }
      return result ?: throw NullPointerException("Venues: venue")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface Venue : Entity {
  override val id: ID

  override val name: String

  override val description: String?

  public val parent: Venue?

  public val links: List<SocialLink>

  override val images: List<Image>

  override val tags: List<Tag>

  public val children: List<Venue>

  public val location: GPSCoordinates?

  public val address: Address?

  override val events: EventSearchResults

  override val path: String

  public val status: VenueStatus

  public val redirect: String?

  public val ticketsUrl: String?

  public val eventStreamId: String?

  public val parking: String?
}

public class VenueResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : Entity,
    Venue {
  override val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val id: ID
    get() {
      _response.throwIfError("id")
      val result = element["id"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("Venue: id")
    }

  override val name: String
    get() {
      _response.throwIfError("name")
      val result = element["name"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("Venue: name")
    }

  override val description: String?
    get() {
      _response.throwIfError("description")
      val result = element["description"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val parent: Venue?
    get() {
      _response.throwIfError("parent")
      val result = element["parent"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          VenueResponse(_response.forElement("parent"), it)
        }
      }
      return result
    }

  override val links: List<SocialLink>
    get() {
      _response.throwIfError("links")
      val result = element["links"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            SocialLinkResponse(_response.forElement("links"), it)
          }
        }
      }
      return result ?: throw NullPointerException("Venue: links")
    }

  override val images: List<Image>
    get() {
      _response.throwIfError("images")
      val result = element["images"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            ImageResponse(_response.forElement("images"), it)
          }
        }
      }
      return result ?: throw NullPointerException("Venue: images")
    }

  override val tags: List<Tag>
    get() {
      _response.throwIfError("tags")
      val result = element["tags"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            TagResponse(_response.forElement("tags"), it)
          }
        }
      }
      return result ?: throw NullPointerException("Venue: tags")
    }

  override val children: List<Venue>
    get() {
      _response.throwIfError("children")
      val result = element["children"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            VenueResponse(_response.forElement("children"), it)
          }
        }
      }
      return result ?: throw NullPointerException("Venue: children")
    }

  override val location: GPSCoordinates?
    get() {
      _response.throwIfError("location")
      val result = element["location"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          GPSCoordinatesResponse(_response.forElement("location"), it)
        }
      }
      return result
    }

  override val address: Address?
    get() {
      _response.throwIfError("address")
      val result = element["address"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AddressResponse(_response.forElement("address"), it)
        }
      }
      return result
    }

  override val events: EventSearchResults
    get() {
      _response.throwIfError("events")
      val result = element["events"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          EventSearchResultsResponse(_response.forElement("events"), it)
        }
      }
      return result ?: throw NullPointerException("Venue: events")
    }

  override val path: String
    get() {
      _response.throwIfError("path")
      val result = element["path"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("Venue: path")
    }

  override val status: VenueStatus
    get() {
      _response.throwIfError("status")
      val result = element["status"]?.takeIf { it !is JsonNull }?.let {
        VenueStatus.valueOf(it.jsonPrimitive.content)
      }
      return result ?: throw NullPointerException("Venue: status")
    }

  override val redirect: String?
    get() {
      _response.throwIfError("redirect")
      val result = element["redirect"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val ticketsUrl: String?
    get() {
      _response.throwIfError("ticketsUrl")
      val result = element["ticketsUrl"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val eventStreamId: String?
    get() {
      _response.throwIfError("eventStreamId")
      val result = element["eventStreamId"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val parking: String?
    get() {
      _response.throwIfError("parking")
      val result = element["parking"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface SocialLink {
  public val type: String

  public val locator: String
}

public class SocialLinkResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : SocialLink {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val type: String
    get() {
      _response.throwIfError("type")
      val result = element["type"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("SocialLink: type")
    }

  override val locator: String
    get() {
      _response.throwIfError("locator")
      val result = element["locator"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("SocialLink: locator")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface Address {
  public val street: String?

  public val city: String?

  public val state: String?

  public val zip: String?

  public val country: String?
}

public class AddressResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : Address {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val street: String?
    get() {
      _response.throwIfError("street")
      val result = element["street"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val city: String?
    get() {
      _response.throwIfError("city")
      val result = element["city"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val state: String?
    get() {
      _response.throwIfError("state")
      val result = element["state"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val zip: String?
    get() {
      _response.throwIfError("zip")
      val result = element["zip"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val country: String?
    get() {
      _response.throwIfError("country")
      val result = element["country"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface GPSCoordinates {
  public val latitude: Float

  public val longitude: Float
}

public class GPSCoordinatesResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : GPSCoordinates {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val latitude: Float
    get() {
      _response.throwIfError("latitude")
      val result = element["latitude"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.floatOrNull
      }
      return result ?: throw NullPointerException("GPSCoordinates: latitude")
    }

  override val longitude: Float
    get() {
      _response.throwIfError("longitude")
      val result = element["longitude"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.floatOrNull
      }
      return result ?: throw NullPointerException("GPSCoordinates: longitude")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface Image {
  public val id: String

  public val uri: String

  public val width: Int?

  public val height: Int?

  public val withWidth: Image

  public val variant: ImageVariant?

  public val variants: List<Image>

  public val small: Image?

  public val medium: Image?

  public val large: Image?
}

public class ImageResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : Image {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val id: String
    get() {
      _response.throwIfError("id")
      val result = element["id"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("Image: id")
    }

  override val uri: String
    get() {
      _response.throwIfError("uri")
      val result = element["uri"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("Image: uri")
    }

  override val width: Int?
    get() {
      _response.throwIfError("width")
      val result = element["width"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.intOrNull
      }
      return result
    }

  override val height: Int?
    get() {
      _response.throwIfError("height")
      val result = element["height"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.intOrNull
      }
      return result
    }

  override val withWidth: Image
    get() {
      _response.throwIfError("withWidth")
      val result = element["withWidth"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          ImageResponse(_response.forElement("withWidth"), it)
        }
      }
      return result ?: throw NullPointerException("Image: withWidth")
    }

  override val variant: ImageVariant?
    get() {
      _response.throwIfError("variant")
      val result = element["variant"]?.takeIf { it !is JsonNull }?.let {
        ImageVariant.valueOf(it.jsonPrimitive.content)
      }
      return result
    }

  override val variants: List<Image>
    get() {
      _response.throwIfError("variants")
      val result = element["variants"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            ImageResponse(_response.forElement("variants"), it)
          }
        }
      }
      return result ?: throw NullPointerException("Image: variants")
    }

  override val small: Image?
    get() {
      _response.throwIfError("small")
      val result = element["small"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          ImageResponse(_response.forElement("small"), it)
        }
      }
      return result
    }

  override val medium: Image?
    get() {
      _response.throwIfError("medium")
      val result = element["medium"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          ImageResponse(_response.forElement("medium"), it)
        }
      }
      return result
    }

  override val large: Image?
    get() {
      _response.throwIfError("large")
      val result = element["large"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          ImageResponse(_response.forElement("large"), it)
        }
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface Tags {
  public val tag: Tag

  public val tags: List<Tag>

  public val root: List<Tag>
}

public class TagsResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : Tags {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val tag: Tag
    get() {
      _response.throwIfError("tag")
      val result = element["tag"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          TagResponse(_response.forElement("tag"), it)
        }
      }
      return result ?: throw NullPointerException("Tags: tag")
    }

  override val tags: List<Tag>
    get() {
      _response.throwIfError("tags")
      val result = element["tags"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            TagResponse(_response.forElement("tags"), it)
          }
        }
      }
      return result ?: throw NullPointerException("Tags: tags")
    }

  override val root: List<Tag>
    get() {
      _response.throwIfError("root")
      val result = element["root"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            TagResponse(_response.forElement("root"), it)
          }
        }
      }
      return result ?: throw NullPointerException("Tags: root")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface Tag : Entity {
  override val id: ID

  override val path: String

  override val name: String

  override val description: String?

  public val pageTitle: String

  override val images: List<Image>

  override val events: EventSearchResults

  override val tags: List<Tag>?

  public val parent: Tag?

  public val shadow: Boolean
}

public class TagResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : Entity,
    Tag {
  override val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val id: ID
    get() {
      _response.throwIfError("id")
      val result = element["id"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("Tag: id")
    }

  override val path: String
    get() {
      _response.throwIfError("path")
      val result = element["path"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("Tag: path")
    }

  override val name: String
    get() {
      _response.throwIfError("name")
      val result = element["name"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("Tag: name")
    }

  override val description: String?
    get() {
      _response.throwIfError("description")
      val result = element["description"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val pageTitle: String
    get() {
      _response.throwIfError("pageTitle")
      val result = element["pageTitle"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("Tag: pageTitle")
    }

  override val images: List<Image>
    get() {
      _response.throwIfError("images")
      val result = element["images"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            ImageResponse(_response.forElement("images"), it)
          }
        }
      }
      return result ?: throw NullPointerException("Tag: images")
    }

  override val events: EventSearchResults
    get() {
      _response.throwIfError("events")
      val result = element["events"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          EventSearchResultsResponse(_response.forElement("events"), it)
        }
      }
      return result ?: throw NullPointerException("Tag: events")
    }

  override val tags: List<Tag>?
    get() {
      _response.throwIfError("tags")
      val result = element["tags"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            TagResponse(_response.forElement("tags"), it)
          }
        }
      }
      return result
    }

  override val parent: Tag?
    get() {
      _response.throwIfError("parent")
      val result = element["parent"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          TagResponse(_response.forElement("parent"), it)
        }
      }
      return result
    }

  override val shadow: Boolean
    get() {
      _response.throwIfError("shadow")
      val result = element["shadow"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.booleanOrNull
      }
      return result ?: throw NullPointerException("Tag: shadow")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface Events {
  public val event: Event

  public val search: EventSearchResults

  public val recentlyActive: EventSearchResults

  public val recentlyActiveByStream: EventSearchResults
}

public class EventsResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : Events {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val event: Event
    get() {
      _response.throwIfError("event")
      val result = element["event"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          EventResponse(_response.forElement("event"), it)
        }
      }
      return result ?: throw NullPointerException("Events: event")
    }

  override val search: EventSearchResults
    get() {
      _response.throwIfError("search")
      val result = element["search"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          EventSearchResultsResponse(_response.forElement("search"), it)
        }
      }
      return result ?: throw NullPointerException("Events: search")
    }

  override val recentlyActive: EventSearchResults
    get() {
      _response.throwIfError("recentlyActive")
      val result = element["recentlyActive"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          EventSearchResultsResponse(_response.forElement("recentlyActive"), it)
        }
      }
      return result ?: throw NullPointerException("Events: recentlyActive")
    }

  override val recentlyActiveByStream: EventSearchResults
    get() {
      _response.throwIfError("recentlyActiveByStream")
      val result = element["recentlyActiveByStream"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          EventSearchResultsResponse(_response.forElement("recentlyActiveByStream"), it)
        }
      }
      return result ?: throw NullPointerException("Events: recentlyActiveByStream")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface Event : Entity {
  override val id: ID

  override val path: String

  override val name: String

  override val description: String?

  public val venue: Venue?

  override val images: List<Image>

  override val tags: List<Tag>

  public val date: AWSDate?

  public val time: AWSTime?

  public val ts: AWSDateTime?

  public val endDate: AWSDate?

  public val endTime: AWSTime?

  public val duration: EventDuration?

  public val performers: List<Performer>

  public val ticketsUrl: String?

  public val disabled: Boolean

  public val sourceUrl: String?

  public val parent: Event?

  public val children: List<Event>

  override val events: EventSearchResults

  public val active: Boolean

  public val tagline: String?

  public val score: Int?

  public val related: EventSearchResults

  public val parking: String?
}

public class EventResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : Entity,
    Event {
  override val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val id: ID
    get() {
      _response.throwIfError("id")
      val result = element["id"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("Event: id")
    }

  override val path: String
    get() {
      _response.throwIfError("path")
      val result = element["path"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("Event: path")
    }

  override val name: String
    get() {
      _response.throwIfError("name")
      val result = element["name"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("Event: name")
    }

  override val description: String?
    get() {
      _response.throwIfError("description")
      val result = element["description"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val venue: Venue?
    get() {
      _response.throwIfError("venue")
      val result = element["venue"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          VenueResponse(_response.forElement("venue"), it)
        }
      }
      return result
    }

  override val images: List<Image>
    get() {
      _response.throwIfError("images")
      val result = element["images"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            ImageResponse(_response.forElement("images"), it)
          }
        }
      }
      return result ?: throw NullPointerException("Event: images")
    }

  override val tags: List<Tag>
    get() {
      _response.throwIfError("tags")
      val result = element["tags"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            TagResponse(_response.forElement("tags"), it)
          }
        }
      }
      return result ?: throw NullPointerException("Event: tags")
    }

  override val date: AWSDate?
    get() {
      _response.throwIfError("date")
      val result = element["date"]?.takeIf { it !is JsonNull }?.let {
        json.decodeFromJsonElement(LocalDateIso8601Serializer, it)
      }
      return result
    }

  override val time: AWSTime?
    get() {
      _response.throwIfError("time")
      val result = element["time"]?.takeIf { it !is JsonNull }?.let {
        json.decodeFromJsonElement(LocalTimeIso8601Serializer, it)
      }
      return result
    }

  override val ts: AWSDateTime?
    get() {
      _response.throwIfError("ts")
      val result = element["ts"]?.takeIf { it !is JsonNull }?.let {
        json.decodeFromJsonElement(InstantIso8601Serializer, it)
      }
      return result
    }

  override val endDate: AWSDate?
    get() {
      _response.throwIfError("endDate")
      val result = element["endDate"]?.takeIf { it !is JsonNull }?.let {
        json.decodeFromJsonElement(LocalDateIso8601Serializer, it)
      }
      return result
    }

  override val endTime: AWSTime?
    get() {
      _response.throwIfError("endTime")
      val result = element["endTime"]?.takeIf { it !is JsonNull }?.let {
        json.decodeFromJsonElement(LocalTimeIso8601Serializer, it)
      }
      return result
    }

  override val duration: EventDuration?
    get() {
      _response.throwIfError("duration")
      val result = element["duration"]?.takeIf { it !is JsonNull }?.let {
        json.decodeFromJsonElement(DurationSerializer, it)
      }
      return result
    }

  override val performers: List<Performer>
    get() {
      _response.throwIfError("performers")
      val result = element["performers"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            PerformerResponse(_response.forElement("performers"), it)
          }
        }
      }
      return result ?: throw NullPointerException("Event: performers")
    }

  override val ticketsUrl: String?
    get() {
      _response.throwIfError("ticketsUrl")
      val result = element["ticketsUrl"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val disabled: Boolean
    get() {
      _response.throwIfError("disabled")
      val result = element["disabled"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.booleanOrNull
      }
      return result ?: throw NullPointerException("Event: disabled")
    }

  override val sourceUrl: String?
    get() {
      _response.throwIfError("sourceUrl")
      val result = element["sourceUrl"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val parent: Event?
    get() {
      _response.throwIfError("parent")
      val result = element["parent"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          EventResponse(_response.forElement("parent"), it)
        }
      }
      return result
    }

  override val children: List<Event>
    get() {
      _response.throwIfError("children")
      val result = element["children"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            EventResponse(_response.forElement("children"), it)
          }
        }
      }
      return result ?: throw NullPointerException("Event: children")
    }

  override val events: EventSearchResults
    get() {
      _response.throwIfError("events")
      val result = element["events"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          EventSearchResultsResponse(_response.forElement("events"), it)
        }
      }
      return result ?: throw NullPointerException("Event: events")
    }

  override val active: Boolean
    get() {
      _response.throwIfError("active")
      val result = element["active"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.booleanOrNull
      }
      return result ?: throw NullPointerException("Event: active")
    }

  override val tagline: String?
    get() {
      _response.throwIfError("tagline")
      val result = element["tagline"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val score: Int?
    get() {
      _response.throwIfError("score")
      val result = element["score"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.intOrNull
      }
      return result
    }

  override val related: EventSearchResults
    get() {
      _response.throwIfError("related")
      val result = element["related"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          EventSearchResultsResponse(_response.forElement("related"), it)
        }
      }
      return result ?: throw NullPointerException("Event: related")
    }

  override val parking: String?
    get() {
      _response.throwIfError("parking")
      val result = element["parking"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface Performers {
  public val performer: Performer

  public val search: PerformerResults
}

public class PerformersResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : Performers {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val performer: Performer
    get() {
      _response.throwIfError("performer")
      val result = element["performer"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          PerformerResponse(_response.forElement("performer"), it)
        }
      }
      return result ?: throw NullPointerException("Performers: performer")
    }

  override val search: PerformerResults
    get() {
      _response.throwIfError("search")
      val result = element["search"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          PerformerResultsResponse(_response.forElement("search"), it)
        }
      }
      return result ?: throw NullPointerException("Performers: search")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface PerformerResults {
  public val performers: List<Performer>?

  public val nextToken: String?
}

public class PerformerResultsResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : PerformerResults {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val performers: List<Performer>?
    get() {
      _response.throwIfError("performers")
      val result = element["performers"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            PerformerResponse(_response.forElement("performers"), it)
          }
        }
      }
      return result
    }

  override val nextToken: String?
    get() {
      _response.throwIfError("nextToken")
      val result = element["nextToken"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface Performer : Entity {
  override val id: ID

  override val path: String

  override val name: String

  override val description: String?

  override val tags: List<Tag>

  override val events: EventSearchResults

  override val images: List<Image>

  public val links: List<SocialLink>
}

public class PerformerResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : Entity,
    Performer {
  override val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val id: ID
    get() {
      _response.throwIfError("id")
      val result = element["id"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("Performer: id")
    }

  override val path: String
    get() {
      _response.throwIfError("path")
      val result = element["path"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("Performer: path")
    }

  override val name: String
    get() {
      _response.throwIfError("name")
      val result = element["name"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("Performer: name")
    }

  override val description: String?
    get() {
      _response.throwIfError("description")
      val result = element["description"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val tags: List<Tag>
    get() {
      _response.throwIfError("tags")
      val result = element["tags"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            TagResponse(_response.forElement("tags"), it)
          }
        }
      }
      return result ?: throw NullPointerException("Performer: tags")
    }

  override val events: EventSearchResults
    get() {
      _response.throwIfError("events")
      val result = element["events"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          EventSearchResultsResponse(_response.forElement("events"), it)
        }
      }
      return result ?: throw NullPointerException("Performer: events")
    }

  override val images: List<Image>
    get() {
      _response.throwIfError("images")
      val result = element["images"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            ImageResponse(_response.forElement("images"), it)
          }
        }
      }
      return result ?: throw NullPointerException("Performer: images")
    }

  override val links: List<SocialLink>
    get() {
      _response.throwIfError("links")
      val result = element["links"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            SocialLinkResponse(_response.forElement("links"), it)
          }
        }
      }
      return result ?: throw NullPointerException("Performer: links")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface ArticleSearchResult {
  public val articles: List<Article>
}

public class ArticleSearchResultResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : ArticleSearchResult {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val articles: List<Article>
    get() {
      _response.throwIfError("articles")
      val result = element["articles"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            ArticleResponse(_response.forElement("articles"), it)
          }
        }
      }
      return result ?: throw NullPointerException("ArticleSearchResult: articles")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface Articles {
  public val article: Article

  public val recent: ArticleSearchResult

  public val query: ArticleSearchResult
}

public class ArticlesResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : Articles {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val article: Article
    get() {
      _response.throwIfError("article")
      val result = element["article"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          ArticleResponse(_response.forElement("article"), it)
        }
      }
      return result ?: throw NullPointerException("Articles: article")
    }

  override val recent: ArticleSearchResult
    get() {
      _response.throwIfError("recent")
      val result = element["recent"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          ArticleSearchResultResponse(_response.forElement("recent"), it)
        }
      }
      return result ?: throw NullPointerException("Articles: recent")
    }

  override val query: ArticleSearchResult
    get() {
      _response.throwIfError("query")
      val result = element["query"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          ArticleSearchResultResponse(_response.forElement("query"), it)
        }
      }
      return result ?: throw NullPointerException("Articles: query")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface Article : Entity {
  override val id: ID

  override val path: String?

  public val title: String

  public val summary: String

  override val description: String?

  override val name: String?

  override val tags: List<Tag>?

  public val content: String

  public val created: AWSDateTime

  public val updated: AWSDateTime

  public val published: AWSDateTime?

  public val linkedEntities: List<Entity>

  override val images: List<Image>

  override val events: EventSearchResults
}

public class ArticleResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : Entity,
    Article {
  override val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val id: ID
    get() {
      _response.throwIfError("id")
      val result = element["id"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("Article: id")
    }

  override val path: String?
    get() {
      _response.throwIfError("path")
      val result = element["path"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val title: String
    get() {
      _response.throwIfError("title")
      val result = element["title"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("Article: title")
    }

  override val summary: String
    get() {
      _response.throwIfError("summary")
      val result = element["summary"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("Article: summary")
    }

  override val description: String?
    get() {
      _response.throwIfError("description")
      val result = element["description"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val name: String?
    get() {
      _response.throwIfError("name")
      val result = element["name"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val tags: List<Tag>?
    get() {
      _response.throwIfError("tags")
      val result = element["tags"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            TagResponse(_response.forElement("tags"), it)
          }
        }
      }
      return result
    }

  override val content: String
    get() {
      _response.throwIfError("content")
      val result = element["content"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("Article: content")
    }

  override val created: AWSDateTime
    get() {
      _response.throwIfError("created")
      val result = element["created"]?.takeIf { it !is JsonNull }?.let {
        json.decodeFromJsonElement(InstantIso8601Serializer, it)
      }
      return result ?: throw NullPointerException("Article: created")
    }

  override val updated: AWSDateTime
    get() {
      _response.throwIfError("updated")
      val result = element["updated"]?.takeIf { it !is JsonNull }?.let {
        json.decodeFromJsonElement(InstantIso8601Serializer, it)
      }
      return result ?: throw NullPointerException("Article: updated")
    }

  override val published: AWSDateTime?
    get() {
      _response.throwIfError("published")
      val result = element["published"]?.takeIf { it !is JsonNull }?.let {
        json.decodeFromJsonElement(InstantIso8601Serializer, it)
      }
      return result
    }

  override val linkedEntities: List<Entity>
    get() {
      _response.throwIfError("linkedEntities")
      val result = element["linkedEntities"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            val typeName = it.get("__typename")?.jsonPrimitive?.contentOrNull
            when (typeName) {
              "Venue" -> VenueResponse(_response.forElement("linkedEntities"), it)
              "Tag" -> TagResponse(_response.forElement("linkedEntities"), it)
              "Event" -> EventResponse(_response.forElement("linkedEntities"), it)
              "Performer" -> PerformerResponse(_response.forElement("linkedEntities"), it)
              "Article" -> ArticleResponse(_response.forElement("linkedEntities"), it)
              "ContentViewEntity" -> ContentViewEntityResponse(_response.forElement("linkedEntities"), it)
              else -> EntityResponse(_response.forElement("linkedEntities"), it)
            }
          }
        }
      }
      return result ?: throw NullPointerException("Article: linkedEntities")
    }

  override val images: List<Image>
    get() {
      _response.throwIfError("images")
      val result = element["images"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            ImageResponse(_response.forElement("images"), it)
          }
        }
      }
      return result ?: throw NullPointerException("Article: images")
    }

  override val events: EventSearchResults
    get() {
      _response.throwIfError("events")
      val result = element["events"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          EventSearchResultsResponse(_response.forElement("events"), it)
        }
      }
      return result ?: throw NullPointerException("Article: events")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface EventSearchResults {
  public val events: List<Event>

  public val pageToken: String?
}

public class EventSearchResultsResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : EventSearchResults {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val events: List<Event>
    get() {
      _response.throwIfError("events")
      val result = element["events"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            EventResponse(_response.forElement("events"), it)
          }
        }
      }
      return result ?: throw NullPointerException("EventSearchResults: events")
    }

  override val pageToken: String?
    get() {
      _response.throwIfError("pageToken")
      val result = element["pageToken"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface SystemMutation {
  public val applicationEvent: Boolean
}

public class SystemMutationResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : SystemMutation {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val applicationEvent: Boolean
    get() {
      _response.throwIfError("applicationEvent")
      val result = element["applicationEvent"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.booleanOrNull
      }
      return result ?: throw NullPointerException("SystemMutation: applicationEvent")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface Subscriptions {
  public val userSubscriptions: UserSubscriptions

  public val mine: UserSubscriptions?
}

public class SubscriptionsResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : Subscriptions {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val userSubscriptions: UserSubscriptions
    get() {
      _response.throwIfError("userSubscriptions")
      val result = element["userSubscriptions"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          UserSubscriptionsResponse(_response.forElement("userSubscriptions"), it)
        }
      }
      return result ?: throw NullPointerException("Subscriptions: userSubscriptions")
    }

  override val mine: UserSubscriptions?
    get() {
      _response.throwIfError("mine")
      val result = element["mine"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          UserSubscriptionsResponse(_response.forElement("mine"), it)
        }
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface UserSubscriptions {
  public val events: EventSearchResults

  public val interactionsWithEntity: List<Subscription>

  public val interactionSummary: InteractionSummary

  public val interactions: List<Subscription>

  public val recommendations: UserRecommendations

  public val calendar: EventSearchResults
}

public class UserSubscriptionsResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : UserSubscriptions {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val events: EventSearchResults
    get() {
      _response.throwIfError("events")
      val result = element["events"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          EventSearchResultsResponse(_response.forElement("events"), it)
        }
      }
      return result ?: throw NullPointerException("UserSubscriptions: events")
    }

  override val interactionsWithEntity: List<Subscription>
    get() {
      _response.throwIfError("interactionsWithEntity")
      val result = element["interactionsWithEntity"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            SubscriptionResponse(_response.forElement("interactionsWithEntity"), it)
          }
        }
      }
      return result ?: throw NullPointerException("UserSubscriptions: interactionsWithEntity")
    }

  override val interactionSummary: InteractionSummary
    get() {
      _response.throwIfError("interactionSummary")
      val result = element["interactionSummary"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          InteractionSummaryResponse(_response.forElement("interactionSummary"), it)
        }
      }
      return result ?: throw NullPointerException("UserSubscriptions: interactionSummary")
    }

  override val interactions: List<Subscription>
    get() {
      _response.throwIfError("interactions")
      val result = element["interactions"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            SubscriptionResponse(_response.forElement("interactions"), it)
          }
        }
      }
      return result ?: throw NullPointerException("UserSubscriptions: interactions")
    }

  override val recommendations: UserRecommendations
    get() {
      _response.throwIfError("recommendations")
      val result = element["recommendations"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          UserRecommendationsResponse(_response.forElement("recommendations"), it)
        }
      }
      return result ?: throw NullPointerException("UserSubscriptions: recommendations")
    }

  override val calendar: EventSearchResults
    get() {
      _response.throwIfError("calendar")
      val result = element["calendar"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          EventSearchResultsResponse(_response.forElement("calendar"), it)
        }
      }
      return result ?: throw NullPointerException("UserSubscriptions: calendar")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface UserRecommendations {
  public val entities: List<UserRecommendation>

  public val top: EventSearchResults

  public val comingUp: List<Event>
}

public class UserRecommendationsResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : UserRecommendations {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val entities: List<UserRecommendation>
    get() {
      _response.throwIfError("entities")
      val result = element["entities"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            UserRecommendationResponse(_response.forElement("entities"), it)
          }
        }
      }
      return result ?: throw NullPointerException("UserRecommendations: entities")
    }

  override val top: EventSearchResults
    get() {
      _response.throwIfError("top")
      val result = element["top"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          EventSearchResultsResponse(_response.forElement("top"), it)
        }
      }
      return result ?: throw NullPointerException("UserRecommendations: top")
    }

  override val comingUp: List<Event>
    get() {
      _response.throwIfError("comingUp")
      val result = element["comingUp"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            EventResponse(_response.forElement("comingUp"), it)
          }
        }
      }
      return result ?: throw NullPointerException("UserRecommendations: comingUp")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface UserRecommendation {
  public val entity: Entity

  public val events: List<Event>

  public val score: Int?
}

public class UserRecommendationResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : UserRecommendation {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val entity: Entity
    get() {
      _response.throwIfError("entity")
      val result = element["entity"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          val typeName = it.get("__typename")?.jsonPrimitive?.contentOrNull
          when (typeName) {
            "Venue" -> VenueResponse(_response.forElement("entity"), it)
            "Tag" -> TagResponse(_response.forElement("entity"), it)
            "Event" -> EventResponse(_response.forElement("entity"), it)
            "Performer" -> PerformerResponse(_response.forElement("entity"), it)
            "Article" -> ArticleResponse(_response.forElement("entity"), it)
            "ContentViewEntity" -> ContentViewEntityResponse(_response.forElement("entity"), it)
            else -> EntityResponse(_response.forElement("entity"), it)
          }
        }
      }
      return result ?: throw NullPointerException("UserRecommendation: entity")
    }

  override val events: List<Event>
    get() {
      _response.throwIfError("events")
      val result = element["events"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            EventResponse(_response.forElement("events"), it)
          }
        }
      }
      return result ?: throw NullPointerException("UserRecommendation: events")
    }

  override val score: Int?
    get() {
      _response.throwIfError("score")
      val result = element["score"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.intOrNull
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface InteractionSummary {
  public val path: String

  public val liked: Boolean
}

public class InteractionSummaryResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : InteractionSummary {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val path: String
    get() {
      _response.throwIfError("path")
      val result = element["path"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("InteractionSummary: path")
    }

  override val liked: Boolean
    get() {
      _response.throwIfError("liked")
      val result = element["liked"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.booleanOrNull
      }
      return result ?: throw NullPointerException("InteractionSummary: liked")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface Subscription {
  public val interactionType: InteractionType

  public val subscriber: String

  public val target: Entity

  public val date: AWSDateTime

  public val name: String

  public val description: String?
}

public class SubscriptionResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : Subscription {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val interactionType: InteractionType
    get() {
      _response.throwIfError("interactionType")
      val result = element["interactionType"]?.takeIf { it !is JsonNull }?.let {
        InteractionType.valueOf(it.jsonPrimitive.content)
      }
      return result ?: throw NullPointerException("Subscription: interactionType")
    }

  override val subscriber: String
    get() {
      _response.throwIfError("subscriber")
      val result = element["subscriber"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("Subscription: subscriber")
    }

  override val target: Entity
    get() {
      _response.throwIfError("target")
      val result = element["target"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          val typeName = it.get("__typename")?.jsonPrimitive?.contentOrNull
          when (typeName) {
            "Venue" -> VenueResponse(_response.forElement("target"), it)
            "Tag" -> TagResponse(_response.forElement("target"), it)
            "Event" -> EventResponse(_response.forElement("target"), it)
            "Performer" -> PerformerResponse(_response.forElement("target"), it)
            "Article" -> ArticleResponse(_response.forElement("target"), it)
            "ContentViewEntity" -> ContentViewEntityResponse(_response.forElement("target"), it)
            else -> EntityResponse(_response.forElement("target"), it)
          }
        }
      }
      return result ?: throw NullPointerException("Subscription: target")
    }

  override val date: AWSDateTime
    get() {
      _response.throwIfError("date")
      val result = element["date"]?.takeIf { it !is JsonNull }?.let {
        json.decodeFromJsonElement(InstantIso8601Serializer, it)
      }
      return result ?: throw NullPointerException("Subscription: date")
    }

  override val name: String
    get() {
      _response.throwIfError("name")
      val result = element["name"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("Subscription: name")
    }

  override val description: String?
    get() {
      _response.throwIfError("description")
      val result = element["description"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface SubscriptionsMutator {
  public val subscribe: Subscription

  public val unsubscribe: Boolean

  public val clear: Boolean

  public val share: Boolean

  public val like: Boolean

  public val interact: Boolean

  public val clearInteraction: Boolean

  public val registerDevice: Boolean
}

public class SubscriptionsMutatorResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : SubscriptionsMutator {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val subscribe: Subscription
    get() {
      _response.throwIfError("subscribe")
      val result = element["subscribe"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          SubscriptionResponse(_response.forElement("subscribe"), it)
        }
      }
      return result ?: throw NullPointerException("SubscriptionsMutator: subscribe")
    }

  override val unsubscribe: Boolean
    get() {
      _response.throwIfError("unsubscribe")
      val result = element["unsubscribe"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.booleanOrNull
      }
      return result ?: throw NullPointerException("SubscriptionsMutator: unsubscribe")
    }

  override val clear: Boolean
    get() {
      _response.throwIfError("clear")
      val result = element["clear"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.booleanOrNull
      }
      return result ?: throw NullPointerException("SubscriptionsMutator: clear")
    }

  override val share: Boolean
    get() {
      _response.throwIfError("share")
      val result = element["share"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.booleanOrNull
      }
      return result ?: throw NullPointerException("SubscriptionsMutator: share")
    }

  override val like: Boolean
    get() {
      _response.throwIfError("like")
      val result = element["like"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.booleanOrNull
      }
      return result ?: throw NullPointerException("SubscriptionsMutator: like")
    }

  override val interact: Boolean
    get() {
      _response.throwIfError("interact")
      val result = element["interact"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.booleanOrNull
      }
      return result ?: throw NullPointerException("SubscriptionsMutator: interact")
    }

  override val clearInteraction: Boolean
    get() {
      _response.throwIfError("clearInteraction")
      val result = element["clearInteraction"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.booleanOrNull
      }
      return result ?: throw NullPointerException("SubscriptionsMutator: clearInteraction")
    }

  override val registerDevice: Boolean
    get() {
      _response.throwIfError("registerDevice")
      val result = element["registerDevice"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.booleanOrNull
      }
      return result ?: throw NullPointerException("SubscriptionsMutator: registerDevice")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface ContentViews {
  public val view: ContentView

  public val byId: ViewConfiguration
}

public class ContentViewsResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : ContentViews {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val view: ContentView
    get() {
      _response.throwIfError("view")
      val result = element["view"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          ContentViewResponse(_response.forElement("view"), it)
        }
      }
      return result ?: throw NullPointerException("ContentViews: view")
    }

  override val byId: ViewConfiguration
    get() {
      _response.throwIfError("byId")
      val result = element["byId"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          ViewConfigurationResponse(_response.forElement("byId"), it)
        }
      }
      return result ?: throw NullPointerException("ContentViews: byId")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface ViewConfiguration {
  public val id: ID

  public val panels: List<ContentPanel>
}

public class ViewConfigurationResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : ViewConfiguration {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val id: ID
    get() {
      _response.throwIfError("id")
      val result = element["id"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("ViewConfiguration: id")
    }

  override val panels: List<ContentPanel>
    get() {
      _response.throwIfError("panels")
      val result = element["panels"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            ContentPanelResponse(_response.forElement("panels"), it)
          }
        }
      }
      return result ?: throw NullPointerException("ViewConfiguration: panels")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface DateRange {
  public val preset: DateRangePreset?

  public val from: String?

  public val to: String?
}

public class DateRangeResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : DateRange {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val preset: DateRangePreset?
    get() {
      _response.throwIfError("preset")
      val result = element["preset"]?.takeIf { it !is JsonNull }?.let {
        DateRangePreset.valueOf(it.jsonPrimitive.content)
      }
      return result
    }

  override val from: String?
    get() {
      _response.throwIfError("from")
      val result = element["from"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val to: String?
    get() {
      _response.throwIfError("to")
      val result = element["to"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface EventQuery {
  public val venues: List<String>?

  public val tags: List<String>?

  public val dateRange: DateRange?

  public val minScore: Float?

  public val limit: Int?
}

public class EventQueryResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : EventQuery {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val venues: List<String>?
    get() {
      _response.throwIfError("venues")
      val result = element["venues"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonPrimitive.content
        }
      }
      return result
    }

  override val tags: List<String>?
    get() {
      _response.throwIfError("tags")
      val result = element["tags"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonPrimitive.content
        }
      }
      return result
    }

  override val dateRange: DateRange?
    get() {
      _response.throwIfError("dateRange")
      val result = element["dateRange"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          DateRangeResponse(_response.forElement("dateRange"), it)
        }
      }
      return result
    }

  override val minScore: Float?
    get() {
      _response.throwIfError("minScore")
      val result = element["minScore"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.floatOrNull
      }
      return result
    }

  override val limit: Int?
    get() {
      _response.throwIfError("limit")
      val result = element["limit"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.intOrNull
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface PanelSource {
  public val entityType: SourceEntityType

  public val mode: SourceMode

  public val manual: List<String>?

  public val eventQuery: EventQuery?

  public val eventQueries: List<EventQuery>?
}

public class PanelSourceResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : PanelSource {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val entityType: SourceEntityType
    get() {
      _response.throwIfError("entityType")
      val result = element["entityType"]?.takeIf { it !is JsonNull }?.let {
        SourceEntityType.valueOf(it.jsonPrimitive.content)
      }
      return result ?: throw NullPointerException("PanelSource: entityType")
    }

  override val mode: SourceMode
    get() {
      _response.throwIfError("mode")
      val result = element["mode"]?.takeIf { it !is JsonNull }?.let {
        SourceMode.valueOf(it.jsonPrimitive.content)
      }
      return result ?: throw NullPointerException("PanelSource: mode")
    }

  override val manual: List<String>?
    get() {
      _response.throwIfError("manual")
      val result = element["manual"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonPrimitive.content
        }
      }
      return result
    }

  override val eventQuery: EventQuery?
    get() {
      _response.throwIfError("eventQuery")
      val result = element["eventQuery"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          EventQueryResponse(_response.forElement("eventQuery"), it)
        }
      }
      return result
    }

  override val eventQueries: List<EventQuery>?
    get() {
      _response.throwIfError("eventQueries")
      val result = element["eventQueries"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            EventQueryResponse(_response.forElement("eventQueries"), it)
          }
        }
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface ContentPanel {
  public val id: ID

  public val type: ContentPanelType

  public val title: String?

  public val titleLink: String?

  public val subTitle: String?

  public val subTitleLink: String?

  public val images: List<Image>?

  public val content: String?

  public val entities: List<Entity>?

  public val entityFilters: List<Entity>?

  public val entitiesSource: EntitySource?

  public val source: PanelSource?

  public val articleSource: String?

  public val links: List<ContentViewLink>?

  public val flags: List<ContentPanelFlag>?

  public val minWidth: Int?

  public val maxWidth: Int?
}

public class ContentPanelResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : ContentPanel {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val id: ID
    get() {
      _response.throwIfError("id")
      val result = element["id"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("ContentPanel: id")
    }

  override val type: ContentPanelType
    get() {
      _response.throwIfError("type")
      val result = element["type"]?.takeIf { it !is JsonNull }?.let {
        ContentPanelType.valueOf(it.jsonPrimitive.content)
      }
      return result ?: throw NullPointerException("ContentPanel: type")
    }

  override val title: String?
    get() {
      _response.throwIfError("title")
      val result = element["title"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val titleLink: String?
    get() {
      _response.throwIfError("titleLink")
      val result = element["titleLink"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val subTitle: String?
    get() {
      _response.throwIfError("subTitle")
      val result = element["subTitle"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val subTitleLink: String?
    get() {
      _response.throwIfError("subTitleLink")
      val result = element["subTitleLink"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val images: List<Image>?
    get() {
      _response.throwIfError("images")
      val result = element["images"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            ImageResponse(_response.forElement("images"), it)
          }
        }
      }
      return result
    }

  override val content: String?
    get() {
      _response.throwIfError("content")
      val result = element["content"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val entities: List<Entity>?
    get() {
      _response.throwIfError("entities")
      val result = element["entities"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            val typeName = it.get("__typename")?.jsonPrimitive?.contentOrNull
            when (typeName) {
              "Venue" -> VenueResponse(_response.forElement("entities"), it)
              "Tag" -> TagResponse(_response.forElement("entities"), it)
              "Event" -> EventResponse(_response.forElement("entities"), it)
              "Performer" -> PerformerResponse(_response.forElement("entities"), it)
              "Article" -> ArticleResponse(_response.forElement("entities"), it)
              "ContentViewEntity" -> ContentViewEntityResponse(_response.forElement("entities"), it)
              else -> EntityResponse(_response.forElement("entities"), it)
            }
          }
        }
      }
      return result
    }

  override val entityFilters: List<Entity>?
    get() {
      _response.throwIfError("entityFilters")
      val result = element["entityFilters"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            val typeName = it.get("__typename")?.jsonPrimitive?.contentOrNull
            when (typeName) {
              "Venue" -> VenueResponse(_response.forElement("entityFilters"), it)
              "Tag" -> TagResponse(_response.forElement("entityFilters"), it)
              "Event" -> EventResponse(_response.forElement("entityFilters"), it)
              "Performer" -> PerformerResponse(_response.forElement("entityFilters"), it)
              "Article" -> ArticleResponse(_response.forElement("entityFilters"), it)
              "ContentViewEntity" -> ContentViewEntityResponse(_response.forElement("entityFilters"), it)
              else -> EntityResponse(_response.forElement("entityFilters"), it)
            }
          }
        }
      }
      return result
    }

  override val entitiesSource: EntitySource?
    get() {
      _response.throwIfError("entitiesSource")
      val result = element["entitiesSource"]?.takeIf { it !is JsonNull }?.let {
        EntitySource.valueOf(it.jsonPrimitive.content)
      }
      return result
    }

  override val source: PanelSource?
    get() {
      _response.throwIfError("source")
      val result = element["source"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          PanelSourceResponse(_response.forElement("source"), it)
        }
      }
      return result
    }

  override val articleSource: String?
    get() {
      _response.throwIfError("articleSource")
      val result = element["articleSource"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val links: List<ContentViewLink>?
    get() {
      _response.throwIfError("links")
      val result = element["links"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            ContentViewLinkResponse(_response.forElement("links"), it)
          }
        }
      }
      return result
    }

  override val flags: List<ContentPanelFlag>?
    get() {
      _response.throwIfError("flags")
      val result = element["flags"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          ContentPanelFlag.valueOf(it.jsonPrimitive.content)
        }
      }
      return result
    }

  override val minWidth: Int?
    get() {
      _response.throwIfError("minWidth")
      val result = element["minWidth"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.intOrNull
      }
      return result
    }

  override val maxWidth: Int?
    get() {
      _response.throwIfError("maxWidth")
      val result = element["maxWidth"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.intOrNull
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface ContentView {
  public val title: String?

  public val summary: String?

  public val content: String?

  public val images: List<Image>?

  public val sections: List<ContentViewSection>?

  public val links: List<ContentViewLink>?
}

public class ContentViewResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : ContentView {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val title: String?
    get() {
      _response.throwIfError("title")
      val result = element["title"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val summary: String?
    get() {
      _response.throwIfError("summary")
      val result = element["summary"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val content: String?
    get() {
      _response.throwIfError("content")
      val result = element["content"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val images: List<Image>?
    get() {
      _response.throwIfError("images")
      val result = element["images"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            ImageResponse(_response.forElement("images"), it)
          }
        }
      }
      return result
    }

  override val sections: List<ContentViewSection>?
    get() {
      _response.throwIfError("sections")
      val result = element["sections"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            ContentViewSectionResponse(_response.forElement("sections"), it)
          }
        }
      }
      return result
    }

  override val links: List<ContentViewLink>?
    get() {
      _response.throwIfError("links")
      val result = element["links"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            ContentViewLinkResponse(_response.forElement("links"), it)
          }
        }
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface ContentViewLink {
  public val path: String

  public val name: String

  public val description: String?

  public val type: ContentViewLinkType
}

public class ContentViewLinkResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : ContentViewLink {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val path: String
    get() {
      _response.throwIfError("path")
      val result = element["path"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("ContentViewLink: path")
    }

  override val name: String
    get() {
      _response.throwIfError("name")
      val result = element["name"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("ContentViewLink: name")
    }

  override val description: String?
    get() {
      _response.throwIfError("description")
      val result = element["description"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val type: ContentViewLinkType
    get() {
      _response.throwIfError("type")
      val result = element["type"]?.takeIf { it !is JsonNull }?.let {
        ContentViewLinkType.valueOf(it.jsonPrimitive.content)
      }
      return result ?: throw NullPointerException("ContentViewLink: type")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface ContentViewSection {
  public val type: ContentViewType

  public val title: String

  public val link: String?

  public val content: List<ContentViewEntity>?

  public val source: ContentSource?

  public val contentHtml: String?
}

public class ContentViewSectionResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : ContentViewSection {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val type: ContentViewType
    get() {
      _response.throwIfError("type")
      val result = element["type"]?.takeIf { it !is JsonNull }?.let {
        ContentViewType.valueOf(it.jsonPrimitive.content)
      }
      return result ?: throw NullPointerException("ContentViewSection: type")
    }

  override val title: String
    get() {
      _response.throwIfError("title")
      val result = element["title"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("ContentViewSection: title")
    }

  override val link: String?
    get() {
      _response.throwIfError("link")
      val result = element["link"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val content: List<ContentViewEntity>?
    get() {
      _response.throwIfError("content")
      val result = element["content"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            ContentViewEntityResponse(_response.forElement("content"), it)
          }
        }
      }
      return result
    }

  override val source: ContentSource?
    get() {
      _response.throwIfError("source")
      val result = element["source"]?.takeIf { it !is JsonNull }?.let {
        ContentSource.valueOf(it.jsonPrimitive.content)
      }
      return result
    }

  override val contentHtml: String?
    get() {
      _response.throwIfError("contentHtml")
      val result = element["contentHtml"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface ContentViewEntity : Entity {
  override val id: ID

  override val name: String?

  override val description: String?

  override val images: List<Image>?

  override val tags: List<Tag>?

  override val path: String?

  override val events: EventSearchResults

  public val containerName: String?

  public val containerPath: String?

  public val action: ContentViewEntityAction?
}

public class ContentViewEntityResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : Entity,
    ContentViewEntity {
  override val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val id: ID
    get() {
      _response.throwIfError("id")
      val result = element["id"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("ContentViewEntity: id")
    }

  override val name: String?
    get() {
      _response.throwIfError("name")
      val result = element["name"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val description: String?
    get() {
      _response.throwIfError("description")
      val result = element["description"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val images: List<Image>?
    get() {
      _response.throwIfError("images")
      val result = element["images"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            ImageResponse(_response.forElement("images"), it)
          }
        }
      }
      return result
    }

  override val tags: List<Tag>?
    get() {
      _response.throwIfError("tags")
      val result = element["tags"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            TagResponse(_response.forElement("tags"), it)
          }
        }
      }
      return result
    }

  override val path: String?
    get() {
      _response.throwIfError("path")
      val result = element["path"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val events: EventSearchResults
    get() {
      _response.throwIfError("events")
      val result = element["events"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          EventSearchResultsResponse(_response.forElement("events"), it)
        }
      }
      return result ?: throw NullPointerException("ContentViewEntity: events")
    }

  override val containerName: String?
    get() {
      _response.throwIfError("containerName")
      val result = element["containerName"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val containerPath: String?
    get() {
      _response.throwIfError("containerPath")
      val result = element["containerPath"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val action: ContentViewEntityAction?
    get() {
      _response.throwIfError("action")
      val result = element["action"]?.takeIf { it !is JsonNull }?.let {
        ContentViewEntityAction.valueOf(it.jsonPrimitive.content)
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface Accounts {
  public val me: Account?

  public val createAnonymous: CreateAccountResponse
}

public class AccountsResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : Accounts {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val me: Account?
    get() {
      _response.throwIfError("me")
      val result = element["me"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AccountResponse(_response.forElement("me"), it)
        }
      }
      return result
    }

  override val createAnonymous: CreateAccountResponse
    get() {
      _response.throwIfError("createAnonymous")
      val result = element["createAnonymous"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          CreateAccountResponseResponse(_response.forElement("createAnonymous"), it)
        }
      }
      return result ?: throw NullPointerException("Accounts: createAnonymous")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface CreateAccountResponse {
  public val id: ID

  public val token: String
}

public class CreateAccountResponseResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : CreateAccountResponse {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val id: ID
    get() {
      _response.throwIfError("id")
      val result = element["id"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("CreateAccountResponse: id")
    }

  override val token: String
    get() {
      _response.throwIfError("token")
      val result = element["token"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("CreateAccountResponse: token")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface Account {
  public val createDate: AWSDateTime

  public val registerDevice: Boolean

  public val unregisterDevice: Boolean
}

public class AccountResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : Account {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val createDate: AWSDateTime
    get() {
      _response.throwIfError("createDate")
      val result = element["createDate"]?.takeIf { it !is JsonNull }?.let {
        json.decodeFromJsonElement(InstantIso8601Serializer, it)
      }
      return result ?: throw NullPointerException("Account: createDate")
    }

  override val registerDevice: Boolean
    get() {
      _response.throwIfError("registerDevice")
      val result = element["registerDevice"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.booleanOrNull
      }
      return result ?: throw NullPointerException("Account: registerDevice")
    }

  override val unregisterDevice: Boolean
    get() {
      _response.throwIfError("unregisterDevice")
      val result = element["unregisterDevice"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.booleanOrNull
      }
      return result ?: throw NullPointerException("Account: unregisterDevice")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface ClientSupport {
  public val onboarding: Onboarding

  public val logClientEvent: Boolean
}

public class ClientSupportResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : ClientSupport {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val onboarding: Onboarding
    get() {
      _response.throwIfError("onboarding")
      val result = element["onboarding"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          OnboardingResponse(_response.forElement("onboarding"), it)
        }
      }
      return result ?: throw NullPointerException("ClientSupport: onboarding")
    }

  override val logClientEvent: Boolean
    get() {
      _response.throwIfError("logClientEvent")
      val result = element["logClientEvent"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.booleanOrNull
      }
      return result ?: throw NullPointerException("ClientSupport: logClientEvent")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface Onboarding {
  public val prefSelection: List<OnboardingPref>
}

public class OnboardingResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : Onboarding {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val prefSelection: List<OnboardingPref>
    get() {
      _response.throwIfError("prefSelection")
      val result = element["prefSelection"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            OnboardingPrefResponse(_response.forElement("prefSelection"), it)
          }
        }
      }
      return result ?: throw NullPointerException("Onboarding: prefSelection")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface OnboardingPref {
  public val title: String?

  public val description: String?

  public val entities: List<Entity>
}

public class OnboardingPrefResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : OnboardingPref {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val title: String?
    get() {
      _response.throwIfError("title")
      val result = element["title"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val description: String?
    get() {
      _response.throwIfError("description")
      val result = element["description"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val entities: List<Entity>
    get() {
      _response.throwIfError("entities")
      val result = element["entities"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            val typeName = it.get("__typename")?.jsonPrimitive?.contentOrNull
            when (typeName) {
              "Venue" -> VenueResponse(_response.forElement("entities"), it)
              "Tag" -> TagResponse(_response.forElement("entities"), it)
              "Event" -> EventResponse(_response.forElement("entities"), it)
              "Performer" -> PerformerResponse(_response.forElement("entities"), it)
              "Article" -> ArticleResponse(_response.forElement("entities"), it)
              "ContentViewEntity" -> ContentViewEntityResponse(_response.forElement("entities"), it)
              else -> EntityResponse(_response.forElement("entities"), it)
            }
          }
        }
      }
      return result ?: throw NullPointerException("OnboardingPref: entities")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface AuxContent {
  public val onboarding: Onboarding

  public val features: AuxFeatures
}

public class AuxContentResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : AuxContent {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val onboarding: Onboarding
    get() {
      _response.throwIfError("onboarding")
      val result = element["onboarding"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          OnboardingResponse(_response.forElement("onboarding"), it)
        }
      }
      return result ?: throw NullPointerException("AuxContent: onboarding")
    }

  override val features: AuxFeatures
    get() {
      _response.throwIfError("features")
      val result = element["features"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AuxFeaturesResponse(_response.forElement("features"), it)
        }
      }
      return result ?: throw NullPointerException("AuxContent: features")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface AuxFeatures {
  public val feature: ViewConfiguration
}

public class AuxFeaturesResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : AuxFeatures {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val feature: ViewConfiguration
    get() {
      _response.throwIfError("feature")
      val result = element["feature"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          ViewConfigurationResponse(_response.forElement("feature"), it)
        }
      }
      return result ?: throw NullPointerException("AuxFeatures: feature")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface AdminMutation {
  public val venues: AdminVenues

  public val events: AdminEvents

  public val performers: AdminPerformers

  public val articles: AdminArticles

  public val tags: AdminTags

  public val images: AdminImages

  public val loaders: AdminLoaders

  public val logs: AdminLogs

  public val domain: String?

  public val auxContent: AdminContentViews
}

public class AdminMutationResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : AdminMutation {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val venues: AdminVenues
    get() {
      _response.throwIfError("venues")
      val result = element["venues"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminVenuesResponse(_response.forElement("venues"), it)
        }
      }
      return result ?: throw NullPointerException("AdminMutation: venues")
    }

  override val events: AdminEvents
    get() {
      _response.throwIfError("events")
      val result = element["events"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminEventsResponse(_response.forElement("events"), it)
        }
      }
      return result ?: throw NullPointerException("AdminMutation: events")
    }

  override val performers: AdminPerformers
    get() {
      _response.throwIfError("performers")
      val result = element["performers"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminPerformersResponse(_response.forElement("performers"), it)
        }
      }
      return result ?: throw NullPointerException("AdminMutation: performers")
    }

  override val articles: AdminArticles
    get() {
      _response.throwIfError("articles")
      val result = element["articles"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminArticlesResponse(_response.forElement("articles"), it)
        }
      }
      return result ?: throw NullPointerException("AdminMutation: articles")
    }

  override val tags: AdminTags
    get() {
      _response.throwIfError("tags")
      val result = element["tags"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminTagsResponse(_response.forElement("tags"), it)
        }
      }
      return result ?: throw NullPointerException("AdminMutation: tags")
    }

  override val images: AdminImages
    get() {
      _response.throwIfError("images")
      val result = element["images"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminImagesResponse(_response.forElement("images"), it)
        }
      }
      return result ?: throw NullPointerException("AdminMutation: images")
    }

  override val loaders: AdminLoaders
    get() {
      _response.throwIfError("loaders")
      val result = element["loaders"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminLoadersResponse(_response.forElement("loaders"), it)
        }
      }
      return result ?: throw NullPointerException("AdminMutation: loaders")
    }

  override val logs: AdminLogs
    get() {
      _response.throwIfError("logs")
      val result = element["logs"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminLogsResponse(_response.forElement("logs"), it)
        }
      }
      return result ?: throw NullPointerException("AdminMutation: logs")
    }

  override val domain: String?
    get() {
      _response.throwIfError("domain")
      val result = element["domain"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val auxContent: AdminContentViews
    get() {
      _response.throwIfError("auxContent")
      val result = element["auxContent"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminContentViewsResponse(_response.forElement("auxContent"), it)
        }
      }
      return result ?: throw NullPointerException("AdminMutation: auxContent")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface AdminLogs {
  public val startQuery: ID

  public val results: LogQueryResult
}

public class AdminLogsResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : AdminLogs {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val startQuery: ID
    get() {
      _response.throwIfError("startQuery")
      val result = element["startQuery"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("AdminLogs: startQuery")
    }

  override val results: LogQueryResult
    get() {
      _response.throwIfError("results")
      val result = element["results"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          LogQueryResultResponse(_response.forElement("results"), it)
        }
      }
      return result ?: throw NullPointerException("AdminLogs: results")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface LogQueryResult {
  public val status: String

  public val rows: List<LogQueryRow>

  public val statistics: LogQueryStatistics?
}

public class LogQueryResultResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : LogQueryResult {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val status: String
    get() {
      _response.throwIfError("status")
      val result = element["status"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("LogQueryResult: status")
    }

  override val rows: List<LogQueryRow>
    get() {
      _response.throwIfError("rows")
      val result = element["rows"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            LogQueryRowResponse(_response.forElement("rows"), it)
          }
        }
      }
      return result ?: throw NullPointerException("LogQueryResult: rows")
    }

  override val statistics: LogQueryStatistics?
    get() {
      _response.throwIfError("statistics")
      val result = element["statistics"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          LogQueryStatisticsResponse(_response.forElement("statistics"), it)
        }
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface LogQueryRow {
  public val fields: List<LogQueryField>
}

public class LogQueryRowResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : LogQueryRow {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val fields: List<LogQueryField>
    get() {
      _response.throwIfError("fields")
      val result = element["fields"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            LogQueryFieldResponse(_response.forElement("fields"), it)
          }
        }
      }
      return result ?: throw NullPointerException("LogQueryRow: fields")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface LogQueryField {
  public val `field`: String

  public val `value`: String?
}

public class LogQueryFieldResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : LogQueryField {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val `field`: String
    get() {
      _response.throwIfError("field")
      val result = element["field"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("LogQueryField: field")
    }

  override val `value`: String?
    get() {
      _response.throwIfError("value")
      val result = element["value"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface LogQueryStatistics {
  public val recordsMatched: Float?

  public val recordsScanned: Float?
}

public class LogQueryStatisticsResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : LogQueryStatistics {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val recordsMatched: Float?
    get() {
      _response.throwIfError("recordsMatched")
      val result = element["recordsMatched"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.floatOrNull
      }
      return result
    }

  override val recordsScanned: Float?
    get() {
      _response.throwIfError("recordsScanned")
      val result = element["recordsScanned"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.floatOrNull
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface AdminImages {
  public val upload: Image
}

public class AdminImagesResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : AdminImages {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val upload: Image
    get() {
      _response.throwIfError("upload")
      val result = element["upload"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          ImageResponse(_response.forElement("upload"), it)
        }
      }
      return result ?: throw NullPointerException("AdminImages: upload")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface AdminVenues {
  public val venue: AdminVenue

  public val add: AdminVenue
}

public class AdminVenuesResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : AdminVenues {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val venue: AdminVenue
    get() {
      _response.throwIfError("venue")
      val result = element["venue"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminVenueResponse(_response.forElement("venue"), it)
        }
      }
      return result ?: throw NullPointerException("AdminVenues: venue")
    }

  override val add: AdminVenue
    get() {
      _response.throwIfError("add")
      val result = element["add"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminVenueResponse(_response.forElement("add"), it)
        }
      }
      return result ?: throw NullPointerException("AdminVenues: add")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface AdminVenue {
  public val id: ID

  public val parent: Venue?

  public val children: List<Venue>

  public val layers: List<AdminVenueLayer>

  public val layer: AdminVenueLayer?

  public val local: AdminVenueLayer

  public val resolved: Venue
}

public class AdminVenueResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : AdminVenue {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val id: ID
    get() {
      _response.throwIfError("id")
      val result = element["id"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("AdminVenue: id")
    }

  override val parent: Venue?
    get() {
      _response.throwIfError("parent")
      val result = element["parent"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          VenueResponse(_response.forElement("parent"), it)
        }
      }
      return result
    }

  override val children: List<Venue>
    get() {
      _response.throwIfError("children")
      val result = element["children"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            VenueResponse(_response.forElement("children"), it)
          }
        }
      }
      return result ?: throw NullPointerException("AdminVenue: children")
    }

  override val layers: List<AdminVenueLayer>
    get() {
      _response.throwIfError("layers")
      val result = element["layers"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            AdminVenueLayerResponse(_response.forElement("layers"), it)
          }
        }
      }
      return result ?: throw NullPointerException("AdminVenue: layers")
    }

  override val layer: AdminVenueLayer?
    get() {
      _response.throwIfError("layer")
      val result = element["layer"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminVenueLayerResponse(_response.forElement("layer"), it)
        }
      }
      return result
    }

  override val local: AdminVenueLayer
    get() {
      _response.throwIfError("local")
      val result = element["local"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminVenueLayerResponse(_response.forElement("local"), it)
        }
      }
      return result ?: throw NullPointerException("AdminVenue: local")
    }

  override val resolved: Venue
    get() {
      _response.throwIfError("resolved")
      val result = element["resolved"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          VenueResponse(_response.forElement("resolved"), it)
        }
      }
      return result ?: throw NullPointerException("AdminVenue: resolved")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface AdminVenueLayer {
  public val name: String?

  public val description: String?

  public val address: Address?

  public val location: GPSCoordinates?

  public val links: List<SocialLink>?

  public val tags: List<Tag>?

  public val images: List<Image>

  public val aliases: List<String>?

  public val ticketsUrl: String?

  public val redirect: Venue?

  public val status: VenueStatus?

  public val update: AdminVenueLayer

  public val rankingScore: Int?

  public val eventStreamId: String?

  public val parking: String?
}

public class AdminVenueLayerResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : AdminVenueLayer {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val name: String?
    get() {
      _response.throwIfError("name")
      val result = element["name"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val description: String?
    get() {
      _response.throwIfError("description")
      val result = element["description"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val address: Address?
    get() {
      _response.throwIfError("address")
      val result = element["address"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AddressResponse(_response.forElement("address"), it)
        }
      }
      return result
    }

  override val location: GPSCoordinates?
    get() {
      _response.throwIfError("location")
      val result = element["location"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          GPSCoordinatesResponse(_response.forElement("location"), it)
        }
      }
      return result
    }

  override val links: List<SocialLink>?
    get() {
      _response.throwIfError("links")
      val result = element["links"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            SocialLinkResponse(_response.forElement("links"), it)
          }
        }
      }
      return result
    }

  override val tags: List<Tag>?
    get() {
      _response.throwIfError("tags")
      val result = element["tags"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            TagResponse(_response.forElement("tags"), it)
          }
        }
      }
      return result
    }

  override val images: List<Image>
    get() {
      _response.throwIfError("images")
      val result = element["images"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            ImageResponse(_response.forElement("images"), it)
          }
        }
      }
      return result ?: throw NullPointerException("AdminVenueLayer: images")
    }

  override val aliases: List<String>?
    get() {
      _response.throwIfError("aliases")
      val result = element["aliases"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonPrimitive.content
        }
      }
      return result
    }

  override val ticketsUrl: String?
    get() {
      _response.throwIfError("ticketsUrl")
      val result = element["ticketsUrl"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val redirect: Venue?
    get() {
      _response.throwIfError("redirect")
      val result = element["redirect"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          VenueResponse(_response.forElement("redirect"), it)
        }
      }
      return result
    }

  override val status: VenueStatus?
    get() {
      _response.throwIfError("status")
      val result = element["status"]?.takeIf { it !is JsonNull }?.let {
        VenueStatus.valueOf(it.jsonPrimitive.content)
      }
      return result
    }

  override val update: AdminVenueLayer
    get() {
      _response.throwIfError("update")
      val result = element["update"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminVenueLayerResponse(_response.forElement("update"), it)
        }
      }
      return result ?: throw NullPointerException("AdminVenueLayer: update")
    }

  override val rankingScore: Int?
    get() {
      _response.throwIfError("rankingScore")
      val result = element["rankingScore"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.intOrNull
      }
      return result
    }

  override val eventStreamId: String?
    get() {
      _response.throwIfError("eventStreamId")
      val result = element["eventStreamId"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val parking: String?
    get() {
      _response.throwIfError("parking")
      val result = element["parking"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface AdminPerformers {
  public val performer: AdminPerformer

  public val add: AdminPerformer

  public val enhance: PerformerUpdate?
}

public class AdminPerformersResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : AdminPerformers {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val performer: AdminPerformer
    get() {
      _response.throwIfError("performer")
      val result = element["performer"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminPerformerResponse(_response.forElement("performer"), it)
        }
      }
      return result ?: throw NullPointerException("AdminPerformers: performer")
    }

  override val add: AdminPerformer
    get() {
      _response.throwIfError("add")
      val result = element["add"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminPerformerResponse(_response.forElement("add"), it)
        }
      }
      return result ?: throw NullPointerException("AdminPerformers: add")
    }

  override val enhance: PerformerUpdate?
    get() {
      _response.throwIfError("enhance")
      val result = element["enhance"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          PerformerUpdateResponse(_response.forElement("enhance"), it)
        }
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface SourceDataSearchResult {
  public val id: ID

  public val description: String?
}

public class SourceDataSearchResultResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : SourceDataSearchResult {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val id: ID
    get() {
      _response.throwIfError("id")
      val result = element["id"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("SourceDataSearchResult: id")
    }

  override val description: String?
    get() {
      _response.throwIfError("description")
      val result = element["description"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface AdminPerformer {
  public val id: ID

  public val layers: List<AdminPerformerLayer?>

  public val layer: AdminPerformerLayer?

  public val local: AdminPerformerLayer

  public val resolved: Performer
}

public class AdminPerformerResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : AdminPerformer {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val id: ID
    get() {
      _response.throwIfError("id")
      val result = element["id"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("AdminPerformer: id")
    }

  override val layers: List<AdminPerformerLayer?>
    get() {
      _response.throwIfError("layers")
      val result = element["layers"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            AdminPerformerLayerResponse(_response.forElement("layers"), it)
          }
        }
      }
      return result ?: throw NullPointerException("AdminPerformer: layers")
    }

  override val layer: AdminPerformerLayer?
    get() {
      _response.throwIfError("layer")
      val result = element["layer"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminPerformerLayerResponse(_response.forElement("layer"), it)
        }
      }
      return result
    }

  override val local: AdminPerformerLayer
    get() {
      _response.throwIfError("local")
      val result = element["local"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminPerformerLayerResponse(_response.forElement("local"), it)
        }
      }
      return result ?: throw NullPointerException("AdminPerformer: local")
    }

  override val resolved: Performer
    get() {
      _response.throwIfError("resolved")
      val result = element["resolved"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          PerformerResponse(_response.forElement("resolved"), it)
        }
      }
      return result ?: throw NullPointerException("AdminPerformer: resolved")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface AdminPerformerLayer {
  public val name: String?

  public val description: String?

  public val images: List<Image>?

  public val aliases: List<String>?

  public val tags: List<Tag>?

  public val wikiIdentifier: String?

  public val rankingScore: Int?

  public val update: AdminPerformerLayer
}

public class AdminPerformerLayerResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : AdminPerformerLayer {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val name: String?
    get() {
      _response.throwIfError("name")
      val result = element["name"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val description: String?
    get() {
      _response.throwIfError("description")
      val result = element["description"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val images: List<Image>?
    get() {
      _response.throwIfError("images")
      val result = element["images"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            ImageResponse(_response.forElement("images"), it)
          }
        }
      }
      return result
    }

  override val aliases: List<String>?
    get() {
      _response.throwIfError("aliases")
      val result = element["aliases"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonPrimitive.content
        }
      }
      return result
    }

  override val tags: List<Tag>?
    get() {
      _response.throwIfError("tags")
      val result = element["tags"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            TagResponse(_response.forElement("tags"), it)
          }
        }
      }
      return result
    }

  override val wikiIdentifier: String?
    get() {
      _response.throwIfError("wikiIdentifier")
      val result = element["wikiIdentifier"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val rankingScore: Int?
    get() {
      _response.throwIfError("rankingScore")
      val result = element["rankingScore"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.intOrNull
      }
      return result
    }

  override val update: AdminPerformerLayer
    get() {
      _response.throwIfError("update")
      val result = element["update"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminPerformerLayerResponse(_response.forElement("update"), it)
        }
      }
      return result ?: throw NullPointerException("AdminPerformerLayer: update")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface PerformerUpdate {
  public val name: String?

  public val description: String?

  public val aliases: List<String>?

  public val tags: List<String>?

  public val images: List<String>?

  public val wikiIdentifier: String?

  public val rankingScore: Int?
}

public class PerformerUpdateResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : PerformerUpdate {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val name: String?
    get() {
      _response.throwIfError("name")
      val result = element["name"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val description: String?
    get() {
      _response.throwIfError("description")
      val result = element["description"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val aliases: List<String>?
    get() {
      _response.throwIfError("aliases")
      val result = element["aliases"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonPrimitive.content
        }
      }
      return result
    }

  override val tags: List<String>?
    get() {
      _response.throwIfError("tags")
      val result = element["tags"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonPrimitive.content
        }
      }
      return result
    }

  override val images: List<String>?
    get() {
      _response.throwIfError("images")
      val result = element["images"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonPrimitive.content
        }
      }
      return result
    }

  override val wikiIdentifier: String?
    get() {
      _response.throwIfError("wikiIdentifier")
      val result = element["wikiIdentifier"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val rankingScore: Int?
    get() {
      _response.throwIfError("rankingScore")
      val result = element["rankingScore"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.intOrNull
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface AdminEvents {
  public val event: AdminEvent

  public val reviewNext: AdminEvent?

  public val add: AdminEvent

  public val forReview: List<Event>

  public val merge: AdminEvent

  public val enhance: EventUpdate?
}

public class AdminEventsResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : AdminEvents {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val event: AdminEvent
    get() {
      _response.throwIfError("event")
      val result = element["event"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminEventResponse(_response.forElement("event"), it)
        }
      }
      return result ?: throw NullPointerException("AdminEvents: event")
    }

  override val reviewNext: AdminEvent?
    get() {
      _response.throwIfError("reviewNext")
      val result = element["reviewNext"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminEventResponse(_response.forElement("reviewNext"), it)
        }
      }
      return result
    }

  override val add: AdminEvent
    get() {
      _response.throwIfError("add")
      val result = element["add"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminEventResponse(_response.forElement("add"), it)
        }
      }
      return result ?: throw NullPointerException("AdminEvents: add")
    }

  override val forReview: List<Event>
    get() {
      _response.throwIfError("forReview")
      val result = element["forReview"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            EventResponse(_response.forElement("forReview"), it)
          }
        }
      }
      return result ?: throw NullPointerException("AdminEvents: forReview")
    }

  override val merge: AdminEvent
    get() {
      _response.throwIfError("merge")
      val result = element["merge"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminEventResponse(_response.forElement("merge"), it)
        }
      }
      return result ?: throw NullPointerException("AdminEvents: merge")
    }

  override val enhance: EventUpdate?
    get() {
      _response.throwIfError("enhance")
      val result = element["enhance"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          EventUpdateResponse(_response.forElement("enhance"), it)
        }
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface AdminEvent {
  public val id: ID

  public val layers: List<AdminEventLayer>

  public val layer: AdminEventLayer?

  public val local: AdminEventLayer

  public val resolved: Event

  public val parent: Event?

  public val children: List<Event>

  public val review: String?

  public val rebuild: Boolean

  public val disable: Boolean

  public val deactivatedTs: AWSDateTime?

  public val activatedTs: AWSDateTime?

  public val generateSocialImage: String

  public val socialImageStatus: SocialImageStatus
}

public class AdminEventResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : AdminEvent {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val id: ID
    get() {
      _response.throwIfError("id")
      val result = element["id"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("AdminEvent: id")
    }

  override val layers: List<AdminEventLayer>
    get() {
      _response.throwIfError("layers")
      val result = element["layers"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            AdminEventLayerResponse(_response.forElement("layers"), it)
          }
        }
      }
      return result ?: throw NullPointerException("AdminEvent: layers")
    }

  override val layer: AdminEventLayer?
    get() {
      _response.throwIfError("layer")
      val result = element["layer"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminEventLayerResponse(_response.forElement("layer"), it)
        }
      }
      return result
    }

  override val local: AdminEventLayer
    get() {
      _response.throwIfError("local")
      val result = element["local"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminEventLayerResponse(_response.forElement("local"), it)
        }
      }
      return result ?: throw NullPointerException("AdminEvent: local")
    }

  override val resolved: Event
    get() {
      _response.throwIfError("resolved")
      val result = element["resolved"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          EventResponse(_response.forElement("resolved"), it)
        }
      }
      return result ?: throw NullPointerException("AdminEvent: resolved")
    }

  override val parent: Event?
    get() {
      _response.throwIfError("parent")
      val result = element["parent"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          EventResponse(_response.forElement("parent"), it)
        }
      }
      return result
    }

  override val children: List<Event>
    get() {
      _response.throwIfError("children")
      val result = element["children"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            EventResponse(_response.forElement("children"), it)
          }
        }
      }
      return result ?: throw NullPointerException("AdminEvent: children")
    }

  override val review: String?
    get() {
      _response.throwIfError("review")
      val result = element["review"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val rebuild: Boolean
    get() {
      _response.throwIfError("rebuild")
      val result = element["rebuild"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.booleanOrNull
      }
      return result ?: throw NullPointerException("AdminEvent: rebuild")
    }

  override val disable: Boolean
    get() {
      _response.throwIfError("disable")
      val result = element["disable"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.booleanOrNull
      }
      return result ?: throw NullPointerException("AdminEvent: disable")
    }

  override val deactivatedTs: AWSDateTime?
    get() {
      _response.throwIfError("deactivatedTs")
      val result = element["deactivatedTs"]?.takeIf { it !is JsonNull }?.let {
        json.decodeFromJsonElement(InstantIso8601Serializer, it)
      }
      return result
    }

  override val activatedTs: AWSDateTime?
    get() {
      _response.throwIfError("activatedTs")
      val result = element["activatedTs"]?.takeIf { it !is JsonNull }?.let {
        json.decodeFromJsonElement(InstantIso8601Serializer, it)
      }
      return result
    }

  override val generateSocialImage: String
    get() {
      _response.throwIfError("generateSocialImage")
      val result = element["generateSocialImage"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("AdminEvent: generateSocialImage")
    }

  override val socialImageStatus: SocialImageStatus
    get() {
      _response.throwIfError("socialImageStatus")
      val result = element["socialImageStatus"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          SocialImageStatusResponse(_response.forElement("socialImageStatus"), it)
        }
      }
      return result ?: throw NullPointerException("AdminEvent: socialImageStatus")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface SocialImageStatus {
  public val state: SocialImageState

  public val url: String?

  public val error: String?
}

public class SocialImageStatusResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : SocialImageStatus {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val state: SocialImageState
    get() {
      _response.throwIfError("state")
      val result = element["state"]?.takeIf { it !is JsonNull }?.let {
        SocialImageState.valueOf(it.jsonPrimitive.content)
      }
      return result ?: throw NullPointerException("SocialImageStatus: state")
    }

  override val url: String?
    get() {
      _response.throwIfError("url")
      val result = element["url"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val error: String?
    get() {
      _response.throwIfError("error")
      val result = element["error"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface AdminEventLayer {
  public val layerId: String?

  public val name: String?

  public val description: String?

  public val date: AWSDate?

  public val time: AWSTime?

  public val duration: Int?

  public val expiration: AWSDateTime?

  public val ticketsUrl: String?

  public val sourceUrl: String?

  public val aliases: List<String>?

  public val images: List<Image>?

  public val excludedImages: List<Image>

  public val tags: List<Tag>?

  public val excludedTags: List<Tag>

  public val performers: List<Performer>?

  public val venue: Venue?

  public val lastUpdated: AWSDateTime?

  public val update: AdminEventLayer

  public val excluded: Boolean?

  public val rankingScore: Int?

  public val tagline: String?

  public val suggestedPerformers: List<String>?
}

public class AdminEventLayerResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : AdminEventLayer {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val layerId: String?
    get() {
      _response.throwIfError("layerId")
      val result = element["layerId"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val name: String?
    get() {
      _response.throwIfError("name")
      val result = element["name"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val description: String?
    get() {
      _response.throwIfError("description")
      val result = element["description"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val date: AWSDate?
    get() {
      _response.throwIfError("date")
      val result = element["date"]?.takeIf { it !is JsonNull }?.let {
        json.decodeFromJsonElement(LocalDateIso8601Serializer, it)
      }
      return result
    }

  override val time: AWSTime?
    get() {
      _response.throwIfError("time")
      val result = element["time"]?.takeIf { it !is JsonNull }?.let {
        json.decodeFromJsonElement(LocalTimeIso8601Serializer, it)
      }
      return result
    }

  override val duration: Int?
    get() {
      _response.throwIfError("duration")
      val result = element["duration"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.intOrNull
      }
      return result
    }

  override val expiration: AWSDateTime?
    get() {
      _response.throwIfError("expiration")
      val result = element["expiration"]?.takeIf { it !is JsonNull }?.let {
        json.decodeFromJsonElement(InstantIso8601Serializer, it)
      }
      return result
    }

  override val ticketsUrl: String?
    get() {
      _response.throwIfError("ticketsUrl")
      val result = element["ticketsUrl"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val sourceUrl: String?
    get() {
      _response.throwIfError("sourceUrl")
      val result = element["sourceUrl"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val aliases: List<String>?
    get() {
      _response.throwIfError("aliases")
      val result = element["aliases"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonPrimitive.content
        }
      }
      return result
    }

  override val images: List<Image>?
    get() {
      _response.throwIfError("images")
      val result = element["images"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            ImageResponse(_response.forElement("images"), it)
          }
        }
      }
      return result
    }

  override val excludedImages: List<Image>
    get() {
      _response.throwIfError("excludedImages")
      val result = element["excludedImages"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            ImageResponse(_response.forElement("excludedImages"), it)
          }
        }
      }
      return result ?: throw NullPointerException("AdminEventLayer: excludedImages")
    }

  override val tags: List<Tag>?
    get() {
      _response.throwIfError("tags")
      val result = element["tags"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            TagResponse(_response.forElement("tags"), it)
          }
        }
      }
      return result
    }

  override val excludedTags: List<Tag>
    get() {
      _response.throwIfError("excludedTags")
      val result = element["excludedTags"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            TagResponse(_response.forElement("excludedTags"), it)
          }
        }
      }
      return result ?: throw NullPointerException("AdminEventLayer: excludedTags")
    }

  override val performers: List<Performer>?
    get() {
      _response.throwIfError("performers")
      val result = element["performers"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            PerformerResponse(_response.forElement("performers"), it)
          }
        }
      }
      return result
    }

  override val venue: Venue?
    get() {
      _response.throwIfError("venue")
      val result = element["venue"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          VenueResponse(_response.forElement("venue"), it)
        }
      }
      return result
    }

  override val lastUpdated: AWSDateTime?
    get() {
      _response.throwIfError("lastUpdated")
      val result = element["lastUpdated"]?.takeIf { it !is JsonNull }?.let {
        json.decodeFromJsonElement(InstantIso8601Serializer, it)
      }
      return result
    }

  override val update: AdminEventLayer
    get() {
      _response.throwIfError("update")
      val result = element["update"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminEventLayerResponse(_response.forElement("update"), it)
        }
      }
      return result ?: throw NullPointerException("AdminEventLayer: update")
    }

  override val excluded: Boolean?
    get() {
      _response.throwIfError("excluded")
      val result = element["excluded"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.booleanOrNull
      }
      return result
    }

  override val rankingScore: Int?
    get() {
      _response.throwIfError("rankingScore")
      val result = element["rankingScore"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.intOrNull
      }
      return result
    }

  override val tagline: String?
    get() {
      _response.throwIfError("tagline")
      val result = element["tagline"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val suggestedPerformers: List<String>?
    get() {
      _response.throwIfError("suggestedPerformers")
      val result = element["suggestedPerformers"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonPrimitive.content
        }
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface EventTag {
  public val id: ID

  public val name: String

  public val confidence: Int?
}

public class EventTagResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : EventTag {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val id: ID
    get() {
      _response.throwIfError("id")
      val result = element["id"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("EventTag: id")
    }

  override val name: String
    get() {
      _response.throwIfError("name")
      val result = element["name"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("EventTag: name")
    }

  override val confidence: Int?
    get() {
      _response.throwIfError("confidence")
      val result = element["confidence"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.intOrNull
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface EventUpdate {
  public val parent: String?

  public val name: String?

  public val description: String?

  public val venue: String?

  public val date: AWSDate?

  public val time: AWSTime?

  public val duration: Int?

  public val endDate: AWSDate?

  public val endTime: AWSTime?

  public val ticketsUrl: String?

  public val sourceUrl: String?

  public val aliases: List<String>?

  public val images: List<String>?

  public val excludedImages: List<String>?

  public val eventTags: List<EventTag>?

  public val excludedTags: List<String>?

  public val performers: List<String>?

  public val excluded: Boolean?

  public val rankingScore: Int?

  public val suggestedPerformers: List<String>?
}

public class EventUpdateResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : EventUpdate {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val parent: String?
    get() {
      _response.throwIfError("parent")
      val result = element["parent"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val name: String?
    get() {
      _response.throwIfError("name")
      val result = element["name"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val description: String?
    get() {
      _response.throwIfError("description")
      val result = element["description"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val venue: String?
    get() {
      _response.throwIfError("venue")
      val result = element["venue"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val date: AWSDate?
    get() {
      _response.throwIfError("date")
      val result = element["date"]?.takeIf { it !is JsonNull }?.let {
        json.decodeFromJsonElement(LocalDateIso8601Serializer, it)
      }
      return result
    }

  override val time: AWSTime?
    get() {
      _response.throwIfError("time")
      val result = element["time"]?.takeIf { it !is JsonNull }?.let {
        json.decodeFromJsonElement(LocalTimeIso8601Serializer, it)
      }
      return result
    }

  override val duration: Int?
    get() {
      _response.throwIfError("duration")
      val result = element["duration"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.intOrNull
      }
      return result
    }

  override val endDate: AWSDate?
    get() {
      _response.throwIfError("endDate")
      val result = element["endDate"]?.takeIf { it !is JsonNull }?.let {
        json.decodeFromJsonElement(LocalDateIso8601Serializer, it)
      }
      return result
    }

  override val endTime: AWSTime?
    get() {
      _response.throwIfError("endTime")
      val result = element["endTime"]?.takeIf { it !is JsonNull }?.let {
        json.decodeFromJsonElement(LocalTimeIso8601Serializer, it)
      }
      return result
    }

  override val ticketsUrl: String?
    get() {
      _response.throwIfError("ticketsUrl")
      val result = element["ticketsUrl"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val sourceUrl: String?
    get() {
      _response.throwIfError("sourceUrl")
      val result = element["sourceUrl"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val aliases: List<String>?
    get() {
      _response.throwIfError("aliases")
      val result = element["aliases"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonPrimitive.content
        }
      }
      return result
    }

  override val images: List<String>?
    get() {
      _response.throwIfError("images")
      val result = element["images"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonPrimitive.content
        }
      }
      return result
    }

  override val excludedImages: List<String>?
    get() {
      _response.throwIfError("excludedImages")
      val result = element["excludedImages"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonPrimitive.content
        }
      }
      return result
    }

  override val eventTags: List<EventTag>?
    get() {
      _response.throwIfError("eventTags")
      val result = element["eventTags"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            EventTagResponse(_response.forElement("eventTags"), it)
          }
        }
      }
      return result
    }

  override val excludedTags: List<String>?
    get() {
      _response.throwIfError("excludedTags")
      val result = element["excludedTags"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonPrimitive.content
        }
      }
      return result
    }

  override val performers: List<String>?
    get() {
      _response.throwIfError("performers")
      val result = element["performers"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonPrimitive.content
        }
      }
      return result
    }

  override val excluded: Boolean?
    get() {
      _response.throwIfError("excluded")
      val result = element["excluded"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.booleanOrNull
      }
      return result
    }

  override val rankingScore: Int?
    get() {
      _response.throwIfError("rankingScore")
      val result = element["rankingScore"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.intOrNull
      }
      return result
    }

  override val suggestedPerformers: List<String>?
    get() {
      _response.throwIfError("suggestedPerformers")
      val result = element["suggestedPerformers"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonPrimitive.content
        }
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface AdminArticles {
  public val article: AdminArticle

  public val new: AdminArticle

  public val drafts: List<AdminArticle>

  public val content: AdminArticle
}

public class AdminArticlesResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : AdminArticles {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val article: AdminArticle
    get() {
      _response.throwIfError("article")
      val result = element["article"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminArticleResponse(_response.forElement("article"), it)
        }
      }
      return result ?: throw NullPointerException("AdminArticles: article")
    }

  override val new: AdminArticle
    get() {
      _response.throwIfError("new")
      val result = element["new"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminArticleResponse(_response.forElement("new"), it)
        }
      }
      return result ?: throw NullPointerException("AdminArticles: new")
    }

  override val drafts: List<AdminArticle>
    get() {
      _response.throwIfError("drafts")
      val result = element["drafts"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            AdminArticleResponse(_response.forElement("drafts"), it)
          }
        }
      }
      return result ?: throw NullPointerException("AdminArticles: drafts")
    }

  override val content: AdminArticle
    get() {
      _response.throwIfError("content")
      val result = element["content"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminArticleResponse(_response.forElement("content"), it)
        }
      }
      return result ?: throw NullPointerException("AdminArticles: content")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface AdminArticle {
  public val id: ID

  public val title: String?

  public val summary: String?

  public val content: String?

  public val links: List<Entity>?

  public val resolvedLinks: List<Entity>

  public val images: List<Image>?

  public val created: AWSDateTime

  public val updated: AWSDateTime

  public val published: AWSDateTime?

  public val update: AdminArticle

  public val publish: AdminArticle

  public val unpublish: AdminArticle

  public val startTs: AWSDateTime?

  public val endTs: AWSDateTime?

  public val delete: Boolean
}

public class AdminArticleResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : AdminArticle {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val id: ID
    get() {
      _response.throwIfError("id")
      val result = element["id"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("AdminArticle: id")
    }

  override val title: String?
    get() {
      _response.throwIfError("title")
      val result = element["title"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val summary: String?
    get() {
      _response.throwIfError("summary")
      val result = element["summary"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val content: String?
    get() {
      _response.throwIfError("content")
      val result = element["content"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val links: List<Entity>?
    get() {
      _response.throwIfError("links")
      val result = element["links"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            val typeName = it.get("__typename")?.jsonPrimitive?.contentOrNull
            when (typeName) {
              "Venue" -> VenueResponse(_response.forElement("links"), it)
              "Tag" -> TagResponse(_response.forElement("links"), it)
              "Event" -> EventResponse(_response.forElement("links"), it)
              "Performer" -> PerformerResponse(_response.forElement("links"), it)
              "Article" -> ArticleResponse(_response.forElement("links"), it)
              "ContentViewEntity" -> ContentViewEntityResponse(_response.forElement("links"), it)
              else -> EntityResponse(_response.forElement("links"), it)
            }
          }
        }
      }
      return result
    }

  override val resolvedLinks: List<Entity>
    get() {
      _response.throwIfError("resolvedLinks")
      val result = element["resolvedLinks"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            val typeName = it.get("__typename")?.jsonPrimitive?.contentOrNull
            when (typeName) {
              "Venue" -> VenueResponse(_response.forElement("resolvedLinks"), it)
              "Tag" -> TagResponse(_response.forElement("resolvedLinks"), it)
              "Event" -> EventResponse(_response.forElement("resolvedLinks"), it)
              "Performer" -> PerformerResponse(_response.forElement("resolvedLinks"), it)
              "Article" -> ArticleResponse(_response.forElement("resolvedLinks"), it)
              "ContentViewEntity" -> ContentViewEntityResponse(_response.forElement("resolvedLinks"), it)
              else -> EntityResponse(_response.forElement("resolvedLinks"), it)
            }
          }
        }
      }
      return result ?: throw NullPointerException("AdminArticle: resolvedLinks")
    }

  override val images: List<Image>?
    get() {
      _response.throwIfError("images")
      val result = element["images"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            ImageResponse(_response.forElement("images"), it)
          }
        }
      }
      return result
    }

  override val created: AWSDateTime
    get() {
      _response.throwIfError("created")
      val result = element["created"]?.takeIf { it !is JsonNull }?.let {
        json.decodeFromJsonElement(InstantIso8601Serializer, it)
      }
      return result ?: throw NullPointerException("AdminArticle: created")
    }

  override val updated: AWSDateTime
    get() {
      _response.throwIfError("updated")
      val result = element["updated"]?.takeIf { it !is JsonNull }?.let {
        json.decodeFromJsonElement(InstantIso8601Serializer, it)
      }
      return result ?: throw NullPointerException("AdminArticle: updated")
    }

  override val published: AWSDateTime?
    get() {
      _response.throwIfError("published")
      val result = element["published"]?.takeIf { it !is JsonNull }?.let {
        json.decodeFromJsonElement(InstantIso8601Serializer, it)
      }
      return result
    }

  override val update: AdminArticle
    get() {
      _response.throwIfError("update")
      val result = element["update"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminArticleResponse(_response.forElement("update"), it)
        }
      }
      return result ?: throw NullPointerException("AdminArticle: update")
    }

  override val publish: AdminArticle
    get() {
      _response.throwIfError("publish")
      val result = element["publish"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminArticleResponse(_response.forElement("publish"), it)
        }
      }
      return result ?: throw NullPointerException("AdminArticle: publish")
    }

  override val unpublish: AdminArticle
    get() {
      _response.throwIfError("unpublish")
      val result = element["unpublish"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminArticleResponse(_response.forElement("unpublish"), it)
        }
      }
      return result ?: throw NullPointerException("AdminArticle: unpublish")
    }

  override val startTs: AWSDateTime?
    get() {
      _response.throwIfError("startTs")
      val result = element["startTs"]?.takeIf { it !is JsonNull }?.let {
        json.decodeFromJsonElement(InstantIso8601Serializer, it)
      }
      return result
    }

  override val endTs: AWSDateTime?
    get() {
      _response.throwIfError("endTs")
      val result = element["endTs"]?.takeIf { it !is JsonNull }?.let {
        json.decodeFromJsonElement(InstantIso8601Serializer, it)
      }
      return result
    }

  override val delete: Boolean
    get() {
      _response.throwIfError("delete")
      val result = element["delete"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.booleanOrNull
      }
      return result ?: throw NullPointerException("AdminArticle: delete")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface AdminTags {
  public val all: List<AdminTag>

  public val tag: AdminTag

  public val add: AdminTag
}

public class AdminTagsResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : AdminTags {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val all: List<AdminTag>
    get() {
      _response.throwIfError("all")
      val result = element["all"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            AdminTagResponse(_response.forElement("all"), it)
          }
        }
      }
      return result ?: throw NullPointerException("AdminTags: all")
    }

  override val tag: AdminTag
    get() {
      _response.throwIfError("tag")
      val result = element["tag"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminTagResponse(_response.forElement("tag"), it)
        }
      }
      return result ?: throw NullPointerException("AdminTags: tag")
    }

  override val add: AdminTag
    get() {
      _response.throwIfError("add")
      val result = element["add"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminTagResponse(_response.forElement("add"), it)
        }
      }
      return result ?: throw NullPointerException("AdminTags: add")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface AdminTag {
  public val id: ID

  public val layers: List<AdminTagLayer?>

  public val layer: AdminTagLayer?

  public val local: AdminTagLayer

  public val resolved: Tag

  public val children: List<Tag>?

  public val parent: Tag?
}

public class AdminTagResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : AdminTag {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val id: ID
    get() {
      _response.throwIfError("id")
      val result = element["id"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("AdminTag: id")
    }

  override val layers: List<AdminTagLayer?>
    get() {
      _response.throwIfError("layers")
      val result = element["layers"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            AdminTagLayerResponse(_response.forElement("layers"), it)
          }
        }
      }
      return result ?: throw NullPointerException("AdminTag: layers")
    }

  override val layer: AdminTagLayer?
    get() {
      _response.throwIfError("layer")
      val result = element["layer"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminTagLayerResponse(_response.forElement("layer"), it)
        }
      }
      return result
    }

  override val local: AdminTagLayer
    get() {
      _response.throwIfError("local")
      val result = element["local"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminTagLayerResponse(_response.forElement("local"), it)
        }
      }
      return result ?: throw NullPointerException("AdminTag: local")
    }

  override val resolved: Tag
    get() {
      _response.throwIfError("resolved")
      val result = element["resolved"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          TagResponse(_response.forElement("resolved"), it)
        }
      }
      return result ?: throw NullPointerException("AdminTag: resolved")
    }

  override val children: List<Tag>?
    get() {
      _response.throwIfError("children")
      val result = element["children"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            TagResponse(_response.forElement("children"), it)
          }
        }
      }
      return result
    }

  override val parent: Tag?
    get() {
      _response.throwIfError("parent")
      val result = element["parent"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          TagResponse(_response.forElement("parent"), it)
        }
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface AdminTagLayer {
  public val name: String?

  public val description: String?

  public val images: List<Image>?

  public val childLimit: Int?

  public val parent: Tag?

  public val shadow: Boolean?

  public val impliedTags: List<Tag>?

  public val pageTitle: String?

  public val rankingScore: Int?

  public val update: AdminTagLayer

  public val aiPromptInput: String?
}

public class AdminTagLayerResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : AdminTagLayer {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val name: String?
    get() {
      _response.throwIfError("name")
      val result = element["name"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val description: String?
    get() {
      _response.throwIfError("description")
      val result = element["description"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val images: List<Image>?
    get() {
      _response.throwIfError("images")
      val result = element["images"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            ImageResponse(_response.forElement("images"), it)
          }
        }
      }
      return result
    }

  override val childLimit: Int?
    get() {
      _response.throwIfError("childLimit")
      val result = element["childLimit"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.intOrNull
      }
      return result
    }

  override val parent: Tag?
    get() {
      _response.throwIfError("parent")
      val result = element["parent"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          TagResponse(_response.forElement("parent"), it)
        }
      }
      return result
    }

  override val shadow: Boolean?
    get() {
      _response.throwIfError("shadow")
      val result = element["shadow"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.booleanOrNull
      }
      return result
    }

  override val impliedTags: List<Tag>?
    get() {
      _response.throwIfError("impliedTags")
      val result = element["impliedTags"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            TagResponse(_response.forElement("impliedTags"), it)
          }
        }
      }
      return result
    }

  override val pageTitle: String?
    get() {
      _response.throwIfError("pageTitle")
      val result = element["pageTitle"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val rankingScore: Int?
    get() {
      _response.throwIfError("rankingScore")
      val result = element["rankingScore"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.intOrNull
      }
      return result
    }

  override val update: AdminTagLayer
    get() {
      _response.throwIfError("update")
      val result = element["update"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminTagLayerResponse(_response.forElement("update"), it)
        }
      }
      return result ?: throw NullPointerException("AdminTagLayer: update")
    }

  override val aiPromptInput: String?
    get() {
      _response.throwIfError("aiPromptInput")
      val result = element["aiPromptInput"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface AdminLoaders {
  public val modules: List<AdminLoaderModule>

  public val module: AdminLoaderModule

  public val loaders: List<AdminLoader>

  public val loader: AdminLoader

  public val registerLoader: AdminLoader

  public val deregister: Boolean
}

public class AdminLoadersResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : AdminLoaders {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val modules: List<AdminLoaderModule>
    get() {
      _response.throwIfError("modules")
      val result = element["modules"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            AdminLoaderModuleResponse(_response.forElement("modules"), it)
          }
        }
      }
      return result ?: throw NullPointerException("AdminLoaders: modules")
    }

  override val module: AdminLoaderModule
    get() {
      _response.throwIfError("module")
      val result = element["module"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminLoaderModuleResponse(_response.forElement("module"), it)
        }
      }
      return result ?: throw NullPointerException("AdminLoaders: module")
    }

  override val loaders: List<AdminLoader>
    get() {
      _response.throwIfError("loaders")
      val result = element["loaders"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            AdminLoaderResponse(_response.forElement("loaders"), it)
          }
        }
      }
      return result ?: throw NullPointerException("AdminLoaders: loaders")
    }

  override val loader: AdminLoader
    get() {
      _response.throwIfError("loader")
      val result = element["loader"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminLoaderResponse(_response.forElement("loader"), it)
        }
      }
      return result ?: throw NullPointerException("AdminLoaders: loader")
    }

  override val registerLoader: AdminLoader
    get() {
      _response.throwIfError("registerLoader")
      val result = element["registerLoader"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminLoaderResponse(_response.forElement("registerLoader"), it)
        }
      }
      return result ?: throw NullPointerException("AdminLoaders: registerLoader")
    }

  override val deregister: Boolean
    get() {
      _response.throwIfError("deregister")
      val result = element["deregister"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.booleanOrNull
      }
      return result ?: throw NullPointerException("AdminLoaders: deregister")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface AdminLoaderModule {
  public val id: ID

  public val name: String
}

public class AdminLoaderModuleResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : AdminLoaderModule {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val id: ID
    get() {
      _response.throwIfError("id")
      val result = element["id"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("AdminLoaderModule: id")
    }

  override val name: String
    get() {
      _response.throwIfError("name")
      val result = element["name"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("AdminLoaderModule: name")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface AdminLoader {
  public val id: ID

  public val name: String

  public val module: AdminLoaderModule?

  public val schedule: String?

  public val update: AdminLoader

  public val parameters: String?

  public val recentExecutions: List<LoaderExecution>

  public val logs: List<LogEvent>

  public val execute: Boolean

  public val publish: Boolean

  public val reprocess: Boolean

  public val readInternalData: String?

  public val saveInternalData: Boolean?

  public val lastLoad: LoaderExecution?
}

public class AdminLoaderResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : AdminLoader {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val id: ID
    get() {
      _response.throwIfError("id")
      val result = element["id"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("AdminLoader: id")
    }

  override val name: String
    get() {
      _response.throwIfError("name")
      val result = element["name"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("AdminLoader: name")
    }

  override val module: AdminLoaderModule?
    get() {
      _response.throwIfError("module")
      val result = element["module"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminLoaderModuleResponse(_response.forElement("module"), it)
        }
      }
      return result
    }

  override val schedule: String?
    get() {
      _response.throwIfError("schedule")
      val result = element["schedule"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val update: AdminLoader
    get() {
      _response.throwIfError("update")
      val result = element["update"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminLoaderResponse(_response.forElement("update"), it)
        }
      }
      return result ?: throw NullPointerException("AdminLoader: update")
    }

  override val parameters: String?
    get() {
      _response.throwIfError("parameters")
      val result = element["parameters"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val recentExecutions: List<LoaderExecution>
    get() {
      _response.throwIfError("recentExecutions")
      val result = element["recentExecutions"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            LoaderExecutionResponse(_response.forElement("recentExecutions"), it)
          }
        }
      }
      return result ?: throw NullPointerException("AdminLoader: recentExecutions")
    }

  override val logs: List<LogEvent>
    get() {
      _response.throwIfError("logs")
      val result = element["logs"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            LogEventResponse(_response.forElement("logs"), it)
          }
        }
      }
      return result ?: throw NullPointerException("AdminLoader: logs")
    }

  override val execute: Boolean
    get() {
      _response.throwIfError("execute")
      val result = element["execute"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.booleanOrNull
      }
      return result ?: throw NullPointerException("AdminLoader: execute")
    }

  override val publish: Boolean
    get() {
      _response.throwIfError("publish")
      val result = element["publish"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.booleanOrNull
      }
      return result ?: throw NullPointerException("AdminLoader: publish")
    }

  override val reprocess: Boolean
    get() {
      _response.throwIfError("reprocess")
      val result = element["reprocess"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.booleanOrNull
      }
      return result ?: throw NullPointerException("AdminLoader: reprocess")
    }

  override val readInternalData: String?
    get() {
      _response.throwIfError("readInternalData")
      val result = element["readInternalData"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  override val saveInternalData: Boolean?
    get() {
      _response.throwIfError("saveInternalData")
      val result = element["saveInternalData"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.booleanOrNull
      }
      return result
    }

  override val lastLoad: LoaderExecution?
    get() {
      _response.throwIfError("lastLoad")
      val result = element["lastLoad"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          LoaderExecutionResponse(_response.forElement("lastLoad"), it)
        }
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface LogEvent {
  public val ts: AWSDateTime

  public val message: String?
}

public class LogEventResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : LogEvent {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val ts: AWSDateTime
    get() {
      _response.throwIfError("ts")
      val result = element["ts"]?.takeIf { it !is JsonNull }?.let {
        json.decodeFromJsonElement(InstantIso8601Serializer, it)
      }
      return result ?: throw NullPointerException("LogEvent: ts")
    }

  override val message: String?
    get() {
      _response.throwIfError("message")
      val result = element["message"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface LoaderExecution {
  public val ts: AWSDateTime

  public val events: String
}

public class LoaderExecutionResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : LoaderExecution {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val ts: AWSDateTime
    get() {
      _response.throwIfError("ts")
      val result = element["ts"]?.takeIf { it !is JsonNull }?.let {
        json.decodeFromJsonElement(InstantIso8601Serializer, it)
      }
      return result ?: throw NullPointerException("LoaderExecution: ts")
    }

  override val events: String
    get() {
      _response.throwIfError("events")
      val result = element["events"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("LoaderExecution: events")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface AdminContentViews {
  public val configuration: AdminContentView

  public val create: AdminContentView
}

public class AdminContentViewsResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : AdminContentViews {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val configuration: AdminContentView
    get() {
      _response.throwIfError("configuration")
      val result = element["configuration"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminContentViewResponse(_response.forElement("configuration"), it)
        }
      }
      return result ?: throw NullPointerException("AdminContentViews: configuration")
    }

  override val create: AdminContentView
    get() {
      _response.throwIfError("create")
      val result = element["create"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          AdminContentViewResponse(_response.forElement("create"), it)
        }
      }
      return result ?: throw NullPointerException("AdminContentViews: create")
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}

public interface AdminContentView {
  public val id: ID

  public val panels: List<ContentPanel>

  public val addPanel: ContentPanel?

  public val editPanel: ContentPanel?

  public val deletePanel: Boolean?
}

public class AdminContentViewResponse(
  private val _response: GraphQLResponse,
  private val element: JsonObject,
) : AdminContentView {
  public val __typename: String
    get() = element["__typename"]!!.jsonPrimitive.content

  override val id: ID
    get() {
      _response.throwIfError("id")
      val result = element["id"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.contentOrNull
      }
      return result ?: throw NullPointerException("AdminContentView: id")
    }

  override val panels: List<ContentPanel>
    get() {
      _response.throwIfError("panels")
      val result = element["panels"]?.takeIf { it !is JsonNull }?.let {
        it.jsonArray.map {
          it.jsonObject.let {
            ContentPanelResponse(_response.forElement("panels"), it)
          }
        }
      }
      return result ?: throw NullPointerException("AdminContentView: panels")
    }

  override val addPanel: ContentPanel?
    get() {
      _response.throwIfError("addPanel")
      val result = element["addPanel"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          ContentPanelResponse(_response.forElement("addPanel"), it)
        }
      }
      return result
    }

  override val editPanel: ContentPanel?
    get() {
      _response.throwIfError("editPanel")
      val result = element["editPanel"]?.takeIf { it !is JsonNull }?.let {
        it.jsonObject.let {
          ContentPanelResponse(_response.forElement("editPanel"), it)
        }
      }
      return result
    }

  override val deletePanel: Boolean?
    get() {
      _response.throwIfError("deletePanel")
      val result = element["deletePanel"]?.takeIf { it !is JsonNull }?.let {
        it.jsonPrimitive.booleanOrNull
      }
      return result
    }

  public fun hasField(key: String): Boolean = element.containsKey(key)
}
